import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import { fetchRealAnalysis, transformToDisplayFormat, checkServicesHealth } from '../services/api';
import PremiumServicesModal from '../components/PremiumServicesModal';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, RadialBarChart, RadialBar } from 'recharts';
import {
  Globe, Lock, Server, Cookie, FileText, Bot, Gauge, MapPin,
  Link2, ArrowRight, Type, Activity, Network, Route, Leaf,
  HardDrive, Search, Info, ShieldCheck, Layers, Share2, Mail,
  Flame, Shield, Archive, TrendingUp, Ban, Bug, Key, Settings,
  Camera, CheckCircle, XCircle, AlertTriangle, Clock, ExternalLink,
  Download, Loader2, ChevronRight, Copy, Zap, AlertCircle, 
  ThumbsUp, ThumbsDown, Lightbulb, ChevronDown, ChevronUp, X,
  Shuffle, Database, Map, Cpu, FileCode, Globe2, Users, Hash,
  GitBranch, Radio, Wifi, List, Sparkles, Package, HelpCircle, Home
} from 'lucide-react';

// Guide descriptions for each card (from AboutPage)
const cardDescriptions = {
  'IP Info': "Un indirizzo IP (Internet Protocol address) è un'etichetta numerica assegnata a ogni dispositivo connesso a una rete. L'IP associato a un dominio può essere trovato interrogando il DNS per il record A del dominio. Trovare l'IP di un server è il primo passo per condurre ulteriori indagini.",
  'SSL Chain': "I certificati SSL autenticano l'identità di un sito web, abilitano comunicazioni crittografate (HTTPS) e stabiliscono fiducia tra client e server. Le informazioni includono l'autorità emittente, il nome del dominio, il periodo di validità e i dettagli dell'organizzazione.",
  'DNS Records': "Il DNS traduce i nomi di dominio in indirizzi IP. I record A e AAAA rivelano gli indirizzi IP, i record MX indicano il provider email, i record TXT sono usati per verifica e sicurezza email (SPF, DKIM, DMARC).",
  'Cookies': "I cookie sono piccoli pezzi di dati memorizzati sul computer dell'utente. Possono rivelare come il sito traccia gli utenti, come vengono gestite le sessioni, e quali framework di analytics vengono utilizzati.",
  'Server Location': "Determina la posizione fisica del server basandosi sull'indirizzo IP. Utile per ottimizzare la consegna dei contenuti, garantire conformità con requisiti di residenza dati, e identificare problemi di latenza.",
  'Quality Metrics': "Misura prestazioni, accessibilità, best practice e SEO usando Lighthouse. Restituisce una checklist di metriche core con punteggi per valutare la qualità complessiva del sito.",
  'HTTP Security Headers': "Gli header di sicurezza HTTP aggiungono protezione contro attacchi comuni. Include HSTS, Content Security Policy, X-Frame-Options e altri header che indicano la postura difensiva del sito.",
  'HTTP Headers': "Gli header HTTP sono coppie chiave-valore che contengono direttive su come gestire i dati trasferiti. Possono rivelare configurazioni del server, tecnologie utilizzate e impostazioni di sicurezza.",
  'Email Security': "Analisi della configurazione email: SPF (Sender Policy Framework), DKIM (DomainKeys Identified Mail), DMARC. Queste protezioni prevengono spoofing email e phishing.",
  'TLS Cipher Suites': "Combinazioni di algoritmi crittografici per connessioni sicure. Una cipher suite è sicura quanto gli algoritmi che contiene. Vulnerabilità note possono rendere la connessione attaccabile.",
  'Open Ports': "Le porte aperte sono endpoint di comunicazione. Ogni porta corrisponde a un servizio (HTTP porta 80, HTTPS porta 443, FTP porta 21). Conoscere le porte aperte rivela i servizi in esecuzione.",
  'Firewall / WAF': "Un WAF protegge le applicazioni web filtrando il traffico HTTP. Protegge da attacchi come XSS, SQL injection, e cross-site forgery. Utile sapere quale WAF è in uso.",
  'HSTS': "HTTP Strict Transport Security protegge contro attacchi man-in-the-middle. Forza i browser a usare solo connessioni HTTPS sicure, proteggendo gli utenti.",
  'DNSSEC': "DNS Security Extensions aggiunge firme crittografiche ai record DNS, permettendo di verificare che le risposte non siano state manomesse. Protegge contro DNS spoofing.",
  'Crawl Rules': "Robots.txt indica quali pagine devono essere ignorate dai crawler. Può rivelare directory che il proprietario non vuole indicizzate, potenzialmente contenenti informazioni sensibili.",
  'Whois Lookup': "WHOIS fornisce informazioni sui proprietari registrati di un dominio: registrant, registrar, date di registrazione e scadenza, nameserver. Fondamentale per identificare i proprietari.",
  'Tech Stack': "Identificazione delle tecnologie: framework frontend, CMS, server web, CDN, analytics. Può rivelare vulnerabilità associate a versioni specifiche di software.",
  'Server Status': "Verifica se il server è online e risponde alle richieste. Mostra codice di stato HTTP, tempo di risposta e percentuale di uptime.",
  'Carbon Footprint': "Calcola l'impronta di carbonio del sito. I siti consumano energia attraverso server, reti e dispositivi utente. Siti ottimizzati hanno impatto ambientale minore.",
  'Global Ranking': "Mostra il ranking globale del sito dai dati Tranco (Umbrella, Majestic, Quantcast, Chrome UX Report, Cloudflare Radar). Utile per capire la popolarità relativa.",
  'Threats & Blocklist': "Verifica se il sito appare in liste di malware e phishing (Google Safe Browsing, VirusTotal). Indica il livello di minaccia e reputazione del sito.",
  'Archive History': "Recupera la storia degli archivi dalla Wayback Machine. Mostra quando il sito è stato catturato, utile per trovare vecchie versioni o contenuti rimossi.",
  'Social Tags': "Meta tag per controllare come il contenuto appare sui social media (Open Graph, Twitter Cards). Rivela come l'organizzazione si presenta pubblicamente.",
  'Screenshot': "Cattura uno screenshot della pagina web. Utile per vedere come appare il sito libero dai vincoli del proprio browser o posizione geografica.",
  'Redirect Chain': "Mostra la catena di redirect dal dominio iniziale alla destinazione finale. Utile per capire come è strutturato il routing del sito e identificare potenziali problemi.",
  'DNS Server': "Informazioni sui server DNS autoritativi per il dominio. Include tempi di risposta e configurazione DNSSEC. Server DNS veloci migliorano le prestazioni.",
  'Sitemap': "Il sitemap XML elenca tutte le pagine indicizzabili del sito. Aiuta i motori di ricerca a scoprire e indicizzare contenuti. Mostra struttura e dimensione del sito.",
  'Site Features': "Analisi delle funzionalità del sito: analytics, CMS, framework, CDN utilizzati. Identifica le tecnologie che alimentano il sito.",
  'Server Info': "Informazioni sul server web: software utilizzato, headers rivelatori, piattaforma. Può rivelare versioni specifiche che hanno vulnerabilità note.",
  'Domain Info': "Informazioni essenziali sul dominio: età, scadenza, registrar, nameserver. Domini più vecchi tendono ad avere maggiore reputazione.",
  'Associated Hosts': "Elenca altri hostname associati allo stesso IP o organizzazione. Utile per mappare l'infrastruttura di un'organizzazione.",
  'TLS Security Config': "Configurazione dettagliata TLS: protocollo, scambio chiavi, cipher, firma certificato. Indica la forza della crittografia.",
  'TLS Handshake': "Dettagli sulla negoziazione TLS: tempo, protocollo, ripresa sessione, OCSP stapling. Indica l'efficienza della connessione sicura.",
  'Block Detection': "Verifica se il dominio appare in blacklist o liste di blocco. Domini bloccati possono indicare attività sospette passate.",
  'Traceroute': "Mostra il percorso dei pacchetti dal server all'utente. Ogni hop rappresenta un router attraversato, con latenza associata.",
  'TXT Records': "I record TXT contengono informazioni testuali per vari scopi: verifica dominio, SPF, DKIM, DMARC. Fondamentali per la sicurezza email.",
  'Linked Pages': "Analisi dei link interni ed esterni sulla pagina. Link interni indicano la struttura del sito, esterni mostrano le connessioni con altri siti.",
  'Security.txt': "File standardizzato che indica come segnalare vulnerabilità di sicurezza. Siti con security.txt dimostrano impegno per la sicurezza."
};

// OpenStreetMap Embed Map Component
const DarkWorldMap = ({ lat, lng, city, country, serverName }) => {
  return (
    <div className="relative h-full w-full bg-[#1a1a2e] rounded-lg overflow-hidden">
      {/* Server label overlay - positioned top-right to avoid zoom buttons */}
      <div className="absolute top-2 right-2 z-10 bg-black/80 px-3 py-1.5 rounded text-white text-xs font-mono">
        Server ({serverName || 'SERVER'})
      </div>
      
      {/* OpenStreetMap iframe - always works without API key */}
      <iframe
        title="Server Location Map"
        width="100%"
        height="100%"
        frameBorder="0"
        scrolling="no"
        style={{ filter: 'invert(1) hue-rotate(180deg) brightness(0.9) contrast(1.1)' }}
        src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng-15},${lat-10},${lng+15},${lat+10}&layer=mapnik&marker=${lat},${lng}`}
      />
      
      {/* Coordinates overlay */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-black/80 px-3 py-1.5 text-xs font-mono text-emerald-400">
        Latitude: {lat.toFixed(5)} &nbsp;&nbsp; Longitude: {lng.toFixed(5)}
      </div>
    </div>
  );
};

// Card component with Info popup
const AnalysisCard = ({ title, icon: Icon, children, status = 'success', size = 'normal' }) => {
  const [showInfo, setShowInfo] = useState(false);
  
  const statusColors = {
    success: 'border-l-emerald-500',
    warning: 'border-l-yellow-500',
    error: 'border-l-red-500',
    info: 'border-l-cyan-500'
  };
  
  const sizeClasses = {
    normal: '',
    large: 'md:col-span-2',
    xlarge: 'md:col-span-2 lg:col-span-3'
  };

  const description = cardDescriptions[title] || `Informazioni su ${title}`;

  return (
    <div className={`bg-[#1a1a2e] rounded-lg border-l-4 ${statusColors[status]} overflow-hidden ${sizeClasses[size]} relative`}>
      {/* Info popup modal */}
      {showInfo && (
        <div className="absolute inset-0 z-50 bg-black/95 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icon className="h-5 w-5 text-cyan-400" />
              <h4 className="font-semibold text-white text-sm">{title}</h4>
            </div>
            <button 
              onClick={() => setShowInfo(false)}
              className="p-1 hover:bg-gray-800 rounded transition-colors"
            >
              <X className="h-4 w-4 text-gray-400 hover:text-white" />
            </button>
          </div>
          <p className="text-xs text-gray-300 leading-relaxed flex-1 overflow-auto">
            {description}
          </p>
          <Link 
            to="/about" 
            className="mt-3 text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
          >
            Vai alla guida completa <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      )}
      
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#0f0f1a] rounded-lg">
              <Icon className="h-5 w-5 text-cyan-400" />
            </div>
            <h3 className="font-semibold text-white text-sm">{title}</h3>
          </div>
          <button 
            onClick={() => setShowInfo(true)}
            className="p-1.5 hover:bg-gray-800 rounded transition-colors"
            title="Informazioni"
          >
            <Info className="h-4 w-4 text-gray-500 hover:text-cyan-400" />
          </button>
        </div>
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
};

const DataItem = ({ label, value, mono = false, status }) => (
  <div className="flex justify-between items-start py-1.5 border-b border-gray-800/50 last:border-0">
    <span className="text-gray-400 text-xs">{label}</span>
    <span className={`text-xs text-right max-w-[60%] flex items-center gap-1 ${mono ? 'font-mono' : ''} ${
      status === 'good' ? 'text-emerald-400' : status === 'bad' ? 'text-red-400' : status === 'warn' ? 'text-yellow-400' : 'text-white'
    }`}>
      {status === 'good' && <CheckCircle className="h-3 w-3" />}
      {status === 'bad' && <XCircle className="h-3 w-3" />}
      {status === 'warn' && <AlertTriangle className="h-3 w-3" />}
      {value}
    </span>
  </div>
);

const Tag = ({ children, color = 'cyan' }) => {
  const colors = {
    cyan: 'bg-cyan-500/20 text-cyan-400',
    green: 'bg-emerald-500/20 text-emerald-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    red: 'bg-red-500/20 text-red-400',
    gray: 'bg-gray-700 text-gray-300'
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[color]}`}>
      {children}
    </span>
  );
};

const PortBadge = ({ port, service, status }) => {
  const colors = {
    open: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    closed: 'bg-red-500/20 text-red-400 border-red-500/30',
    filtered: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
  };
  return (
    <div className={`px-2 py-1 rounded border ${colors[status]} text-xs`}>
      <span className="font-mono">{port}</span>
      <span className="text-gray-400 ml-1">/{service}</span>
    </div>
  );
};

// Security Score Gauge
const SecurityGauge = ({ score, label }) => {
  const getColor = (s) => s >= 80 ? '#10b981' : s >= 60 ? '#f59e0b' : '#ef4444';
  const data = [{ value: score, fill: getColor(score) }];
  
  return (
    <div className="text-center">
      <ResponsiveContainer width="100%" height={120}>
        <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" data={data} startAngle={180} endAngle={0}>
          <RadialBar background dataKey="value" cornerRadius={10} max={100} />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="text-2xl font-bold" style={{ color: getColor(score), marginTop: '-40px' }}>{score}</div>
      <div className="text-xs text-gray-400 mt-1">{label}</div>
    </div>
  );
};

// Analysis Summary Component
const AnalysisSummary = ({ results, domain }) => {
  const [expanded, setExpanded] = useState(true);
  const r = results;
  
  // Early return if no results
  if (!r) return null;
  
  const issues = [];
  const recommendations = [];
  const positives = [];
  
  // Helper for safe access
  const get = (obj, path, def = null) => {
    const keys = path.split('.');
    let result = obj;
    for (const key of keys) {
      result = result?.[key];
      if (result === undefined) return def;
    }
    return result ?? def;
  };
  
  // SSL Analysis
  const sslDays = get(r, 'sslCertificate.data.daysRemaining', 365);
  const sslGrade = get(r, 'sslCertificate.data.grade', 'N/A');
  const ocspStatus = get(r, 'sslCertificate.data.ocsp.status', 'not_checked');
  
  // OCSP Check - Certificate Revocation
  if (ocspStatus === 'revoked') {
    issues.push({ severity: 'critical', text: 'CERTIFICATO SSL REVOCATO - Il certificato è stato invalidato!', category: 'SSL' });
    recommendations.push({ priority: 'alta', text: 'URGENTE: Il certificato è stato revocato. Contattare immediatamente il provider SSL per ottenere un nuovo certificato', category: 'SSL' });
  } else if (ocspStatus === 'good') {
    positives.push({ text: 'Certificato SSL verificato via OCSP (non revocato)', category: 'SSL' });
  }
  
  if (sslDays < 30) {
    issues.push({ severity: 'critical', text: `Certificato SSL scade tra ${sslDays} giorni`, category: 'SSL' });
    recommendations.push({ priority: 'alta', text: 'Rinnovare immediatamente il certificato SSL per evitare interruzioni del servizio', category: 'SSL' });
  } else if (sslDays < 90) {
    issues.push({ severity: 'warning', text: `Certificato SSL scade tra ${sslDays} giorni`, category: 'SSL' });
    recommendations.push({ priority: 'media', text: 'Pianificare il rinnovo del certificato SSL nelle prossime settimane', category: 'SSL' });
  } else if (ocspStatus !== 'revoked') {
    positives.push({ text: `Certificato SSL valido (${sslDays} giorni rimanenti)`, category: 'SSL' });
  }
  
  if ((sslGrade === 'A+' || sslGrade === 'A') && ocspStatus !== 'revoked') {
    positives.push({ text: `Grado SSL eccellente: ${sslGrade}`, category: 'SSL' });
  }

  // DNSSEC - Distinguish between missing DNSSEC and AAAA warnings
  const dnssecEnabled = get(r, 'dnssec.data.enabled', false);
  const dnssecStatus = get(r, 'dnssec.data.status', 'not_configured');
  const nsAaaaIssue = get(r, 'dnssec.data.details.ns_aaaa_issue', false);
  
  if (!dnssecEnabled) {
    if (dnssecStatus === 'warning_aaaa_ns' || nsAaaaIssue) {
      // AAAA warning on NS - less severe
      issues.push({ severity: 'info', text: 'DNSSEC non configurato (alcuni NS senza IPv6)', category: 'DNS' });
      recommendations.push({ priority: 'bassa', text: 'Considerare di abilitare DNSSEC e aggiungere record AAAA ai nameserver', category: 'DNS' });
    } else {
      // Pure DNSSEC not configured
      issues.push({ severity: 'warning', text: 'DNSSEC non abilitato', category: 'DNS' });
      recommendations.push({ priority: 'media', text: 'Abilitare DNSSEC per proteggere contro attacchi DNS spoofing e cache poisoning', category: 'DNS' });
    }
  } else {
    positives.push({ text: 'DNSSEC correttamente configurato', category: 'DNS' });
    // Even if enabled, check for AAAA warning
    if (nsAaaaIssue) {
      issues.push({ severity: 'info', text: 'Alcuni NS senza record AAAA (IPv6)', category: 'DNS' });
    }
  }

  // Security Headers
  const secHeaders = get(r, 'securityHeaders.data.headers', []);
  const missingHeaders = secHeaders.filter(h => !h.present);
  if (missingHeaders.length > 0) {
    issues.push({ severity: 'warning', text: `${missingHeaders.length} header di sicurezza mancanti`, category: 'Headers' });
    missingHeaders.slice(0, 3).forEach(h => {
      recommendations.push({ priority: 'media', text: `Aggiungere header "${h.name}" per migliorare la sicurezza`, category: 'Headers' });
    });
  }
  if (secHeaders.filter(h => h.present).length >= 5) {
    positives.push({ text: 'Buona configurazione degli header di sicurezza', category: 'Headers' });
  }

  // Email Security
  if (!get(r, 'mailConfig.data.spf.exists', false)) {
    issues.push({ severity: 'critical', text: 'Record SPF mancante', category: 'Email' });
    recommendations.push({ priority: 'alta', text: 'Configurare SPF per prevenire spoofing email dal tuo dominio', category: 'Email' });
  }
  if (!get(r, 'mailConfig.data.dmarc.exists', false)) {
    issues.push({ severity: 'warning', text: 'DMARC non configurato', category: 'Email' });
    recommendations.push({ priority: 'media', text: 'Implementare DMARC per proteggere contro phishing e spoofing', category: 'Email' });
  }
  if (!get(r, 'mailConfig.data.dkim.exists', false)) {
    issues.push({ severity: 'warning', text: 'DKIM non configurato', category: 'Email' });
    recommendations.push({ priority: 'media', text: 'Configurare DKIM per autenticare le email in uscita', category: 'Email' });
  }
  if (get(r, 'mailConfig.data.spf.exists') && get(r, 'mailConfig.data.dmarc.exists') && get(r, 'mailConfig.data.dkim.exists')) {
    positives.push({ text: 'Configurazione email completa (SPF, DMARC, DKIM)', category: 'Email' });
  }

  // HSTS
  if (!get(r, 'hsts.data.enabled', false)) {
    issues.push({ severity: 'warning', text: 'HSTS non abilitato', category: 'HTTPS' });
    recommendations.push({ priority: 'media', text: 'Abilitare HSTS per forzare connessioni HTTPS sicure', category: 'HTTPS' });
  } else {
    positives.push({ text: 'HSTS abilitato correttamente', category: 'HTTPS' });
    if (!get(r, 'hsts.data.preload', false)) {
      recommendations.push({ priority: 'bassa', text: 'Considerare l\'aggiunta del sito alla lista HSTS Preload', category: 'HTTPS' });
    }
  }

  // Robots.txt & Sitemap
  if (!get(r, 'robotsTxt.data.exists', false)) {
    issues.push({ severity: 'info', text: 'File robots.txt non trovato', category: 'SEO' });
    recommendations.push({ priority: 'bassa', text: 'Creare un file robots.txt per gestire l\'indicizzazione', category: 'SEO' });
  }
  if (!get(r, 'sitemap.data.exists', false)) {
    issues.push({ severity: 'info', text: 'Sitemap non trovata', category: 'SEO' });
    recommendations.push({ priority: 'bassa', text: 'Creare una sitemap XML per migliorare l\'indicizzazione', category: 'SEO' });
  }

  // Firewall
  if (get(r, 'firewall.data.detected', false)) {
    positives.push({ text: `WAF rilevato: ${get(r, 'firewall.data.type', 'Unknown')}`, category: 'Sicurezza' });
  } else {
    recommendations.push({ priority: 'media', text: 'Considerare l\'implementazione di un Web Application Firewall (WAF)', category: 'Sicurezza' });
  }

  // Performance
  const perfScore = get(r, 'quality.data.lighthouse.performance', 75);
  if (perfScore < 50) {
    issues.push({ severity: 'warning', text: `Performance bassa: ${perfScore}/100`, category: 'Performance' });
    recommendations.push({ priority: 'media', text: 'Ottimizzare le performance del sito (compressione, caching, minificazione)', category: 'Performance' });
  } else if (perfScore >= 80) {
    positives.push({ text: `Ottime performance: ${perfScore}/100`, category: 'Performance' });
  }

  // TLS
  const tlsVersions = get(r, 'tlsCipher.data.versions', []);
  if (tlsVersions.find(v => v.version === 'TLS 1.0' && v.supported)) {
    issues.push({ severity: 'critical', text: 'TLS 1.0 ancora supportato (deprecato)', category: 'TLS' });
    recommendations.push({ priority: 'alta', text: 'Disabilitare TLS 1.0 e 1.1, mantenere solo TLS 1.2 e 1.3', category: 'TLS' });
  }
  if (tlsVersions.find(v => v.version === 'TLS 1.3' && v.supported)) {
    positives.push({ text: 'TLS 1.3 supportato', category: 'TLS' });
  }

  // Security.txt
  if (!get(r, 'securityTxt.data.exists', false)) {
    recommendations.push({ priority: 'bassa', text: 'Aggiungere un file security.txt per facilitare la segnalazione di vulnerabilità', category: 'Sicurezza' });
  } else {
    positives.push({ text: 'Security.txt presente', category: 'Sicurezza' });
  }

  // Calculate overall score
  const criticalCount = issues.filter(i => i.severity === 'critical').length;
  const warningCount = issues.filter(i => i.severity === 'warning').length;
  const infoCount = issues.filter(i => i.severity === 'info').length;
  
  let overallScore = 100;
  overallScore -= criticalCount * 15;
  overallScore -= warningCount * 5;
  overallScore -= infoCount * 2;
  overallScore = Math.max(0, Math.min(100, overallScore));

  const getScoreColor = (score) => score >= 80 ? 'text-emerald-400' : score >= 60 ? 'text-yellow-400' : 'text-red-400';
  const getScoreLabel = (score) => score >= 80 ? 'Buono' : score >= 60 ? 'Discreto' : 'Da Migliorare';

  return (
    <div id="analysis-summary" className="bg-gradient-to-br from-[#1a1a2e] to-[#16162a] rounded-xl border border-gray-800/50 overflow-hidden mt-8">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 bg-red-600/20 rounded-xl">
            <Lightbulb className="h-6 w-6 text-red-500" />
          </div>
          <div className="text-left">
            <h2 className="text-xl font-bold text-white">Analisi Intelligente & Raccomandazioni</h2>
            <p className="text-gray-400 text-sm">Valutazione complessiva della sicurezza e suggerimenti per miglioramenti</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className={`text-3xl font-bold ${getScoreColor(overallScore)}`}>
            {overallScore}/100
          </div>
          {expanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
        </div>
      </button>
      
      {expanded && (
        <div className="px-6 pb-6 space-y-6">
          {/* Score Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-[#0f0f1a] rounded-lg p-4 text-center">
              <div className={`text-4xl font-bold ${getScoreColor(overallScore)}`}>{overallScore}</div>
              <div className="text-sm text-gray-400 mt-1">Punteggio Totale</div>
              <div className={`text-xs mt-1 ${getScoreColor(overallScore)}`}>{getScoreLabel(overallScore)}</div>
            </div>
            <div className="bg-[#0f0f1a] rounded-lg p-4 text-center">
              <div className="text-4xl font-bold text-red-400">{criticalCount}</div>
              <div className="text-sm text-gray-400 mt-1">Problemi Critici</div>
            </div>
            <div className="bg-[#0f0f1a] rounded-lg p-4 text-center">
              <div className="text-4xl font-bold text-yellow-400">{warningCount}</div>
              <div className="text-sm text-gray-400 mt-1">Avvisi</div>
            </div>
            <div className="bg-[#0f0f1a] rounded-lg p-4 text-center">
              <div className="text-4xl font-bold text-emerald-400">{positives.length}</div>
              <div className="text-sm text-gray-400 mt-1">Punti Positivi</div>
            </div>
          </div>

          {/* Issues */}
          {issues.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-400" />
                Problemi Rilevati ({issues.length})
              </h3>
              <div className="space-y-2">
                {issues.map((issue, idx) => (
                  <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${
                    issue.severity === 'critical' ? 'bg-red-500/10 border border-red-500/30' :
                    issue.severity === 'warning' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                    'bg-blue-500/10 border border-blue-500/30'
                  }`}>
                    {issue.severity === 'critical' ? <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" /> :
                     issue.severity === 'warning' ? <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" /> :
                     <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />}
                    <div className="flex-1">
                      <span className="text-white text-sm">{issue.text}</span>
                      <span className="ml-2 text-xs text-gray-500">[{issue.category}]</span>
                    </div>
                    <Tag color={issue.severity === 'critical' ? 'red' : issue.severity === 'warning' ? 'yellow' : 'cyan'}>
                      {issue.severity === 'critical' ? 'Critico' : issue.severity === 'warning' ? 'Avviso' : 'Info'}
                    </Tag>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-400" />
                Raccomandazioni ({recommendations.length})
              </h3>
              <div className="space-y-2">
                {recommendations.sort((a, b) => {
                  const order = { alta: 0, media: 1, bassa: 2 };
                  return order[a.priority] - order[b.priority];
                }).map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-[#0f0f1a] border border-gray-800">
                    <ChevronRight className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                      rec.priority === 'alta' ? 'text-red-400' :
                      rec.priority === 'media' ? 'text-yellow-400' : 'text-gray-400'
                    }`} />
                    <div className="flex-1">
                      <span className="text-gray-300 text-sm">{rec.text}</span>
                      <span className="ml-2 text-xs text-gray-500">[{rec.category}]</span>
                    </div>
                    <Tag color={rec.priority === 'alta' ? 'red' : rec.priority === 'media' ? 'yellow' : 'gray'}>
                      Priorità {rec.priority}
                    </Tag>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Positives */}
          {positives.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <ThumbsUp className="h-5 w-5 text-emerald-400" />
                Aspetti Positivi ({positives.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {positives.map((pos, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <CheckCircle className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-emerald-300 text-sm">{pos.text}</span>
                    <span className="text-xs text-gray-500">[{pos.category}]</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ResultsPage = () => {
  const { domain } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showPdfUpsellBanner, setShowPdfUpsellBanner] = useState(false);

  // Handle PDF download button click - show upsell banner first
  const handlePdfClick = () => {
    setShowPdfUpsellBanner(true);
  };

  // Continue with free PDF download
  const continueFreeDownload = () => {
    setShowPdfUpsellBanner(false);
    generatePDF();
  };

  // Open premium services modal
  const openPremiumServices = () => {
    setShowPdfUpsellBanner(false);
    setShowPremiumModal(true);
  };

  // Generate comprehensive PDF report - with null-safe access
  const generatePDF = () => {
    if (!results || !results.results) return;
    const r = results.results;
    
    // Helper functions for safe access
    const safeGet = (obj, path, def = 'N/A') => {
      const keys = path.split('.');
      let result = obj;
      for (const key of keys) {
        result = result?.[key];
        if (result === undefined || result === null) return def;
      }
      return result;
    };
    const safeArr = (arr) => Array.isArray(arr) ? arr : [];
    
    // Calculate analysis data - SAME LOGIC AS AnalysisSummary component
    const issues = [];
    const recommendations = [];
    const positives = [];
    
    // SSL Analysis
    const sslDays = safeGet(r, 'sslCertificate.data.daysRemaining', 365);
    const sslGrade = safeGet(r, 'sslCertificate.data.grade', 'N/A');
    const ocspStatus = safeGet(r, 'sslCertificate.data.ocsp.status', 'not_checked');
    
    // OCSP Check - Certificate Revocation
    if (ocspStatus === 'revoked') {
      issues.push({ severity: 'critical', text: 'CERTIFICATO SSL REVOCATO - Il certificato è stato invalidato!', category: 'SSL' });
      recommendations.push({ priority: 'alta', text: 'URGENTE: Il certificato è stato revocato. Contattare immediatamente il provider SSL', category: 'SSL' });
    } else if (ocspStatus === 'good') {
      positives.push({ text: 'Certificato SSL verificato via OCSP (non revocato)', category: 'SSL' });
    }
    
    if (sslDays < 30) {
      issues.push({ severity: 'critical', text: `Certificato SSL scade tra ${sslDays} giorni`, category: 'SSL' });
      recommendations.push({ priority: 'alta', text: 'Rinnovare immediatamente il certificato SSL per evitare interruzioni del servizio', category: 'SSL' });
    } else if (sslDays < 90) {
      issues.push({ severity: 'warning', text: `Certificato SSL scade tra ${sslDays} giorni`, category: 'SSL' });
      recommendations.push({ priority: 'media', text: 'Pianificare il rinnovo del certificato SSL nelle prossime settimane', category: 'SSL' });
    } else if (ocspStatus !== 'revoked') {
      positives.push({ text: `Certificato SSL valido (${sslDays} giorni rimanenti)`, category: 'SSL' });
    }
    if ((sslGrade === 'A+' || sslGrade === 'A') && ocspStatus !== 'revoked') {
      positives.push({ text: `Grado SSL eccellente: ${sslGrade}`, category: 'SSL' });
    }
    
    // DNSSEC - Distinguish between missing DNSSEC and AAAA warnings
    const dnssecEnabled = safeGet(r, 'dnssec.data.enabled', false);
    const dnssecStatus = safeGet(r, 'dnssec.data.status', 'not_configured');
    const nsAaaaIssue = safeGet(r, 'dnssec.data.details.ns_aaaa_issue', false);
    
    if (!dnssecEnabled) {
      if (dnssecStatus === 'warning_aaaa_ns' || nsAaaaIssue) {
        // AAAA warning on NS - less severe
        issues.push({ severity: 'info', text: 'DNSSEC non configurato (alcuni NS senza IPv6)', category: 'DNS' });
        recommendations.push({ priority: 'bassa', text: 'Considerare di abilitare DNSSEC e aggiungere record AAAA ai nameserver', category: 'DNS' });
      } else {
        // Pure DNSSEC not configured
        issues.push({ severity: 'warning', text: 'DNSSEC non abilitato', category: 'DNS' });
        recommendations.push({ priority: 'media', text: 'Abilitare DNSSEC per proteggere contro attacchi DNS spoofing e cache poisoning', category: 'DNS' });
      }
    } else {
      positives.push({ text: 'DNSSEC correttamente configurato', category: 'DNS' });
      // Even if enabled, check for AAAA warning
      if (nsAaaaIssue) {
        issues.push({ severity: 'info', text: 'Alcuni NS senza record AAAA (IPv6)', category: 'DNS' });
      }
    }
    
    // Security Headers
    const secHeaders = safeArr(safeGet(r, 'securityHeaders.data.headers', []));
    const missingHeaders = secHeaders.filter(h => !h?.present);
    if (missingHeaders.length > 0) {
      issues.push({ severity: 'warning', text: `${missingHeaders.length} header di sicurezza mancanti`, category: 'Headers' });
      missingHeaders.slice(0, 3).forEach(h => {
        recommendations.push({ priority: 'media', text: `Aggiungere header "${h?.name || 'header'}" per migliorare la sicurezza`, category: 'Headers' });
      });
    }
    if (secHeaders.filter(h => h?.present).length >= 5) {
      positives.push({ text: 'Buona configurazione degli header di sicurezza', category: 'Headers' });
    }
    
    // Email Security
    if (!safeGet(r, 'mailConfig.data.spf.exists', false)) {
      issues.push({ severity: 'critical', text: 'Record SPF mancante', category: 'Email' });
      recommendations.push({ priority: 'alta', text: 'Configurare SPF per prevenire spoofing email dal tuo dominio', category: 'Email' });
    }
    if (!safeGet(r, 'mailConfig.data.dmarc.exists', false)) {
      issues.push({ severity: 'warning', text: 'DMARC non configurato', category: 'Email' });
      recommendations.push({ priority: 'media', text: 'Implementare DMARC per proteggere contro phishing e spoofing', category: 'Email' });
    }
    if (!safeGet(r, 'mailConfig.data.dkim.exists', false)) {
      issues.push({ severity: 'warning', text: 'DKIM non configurato', category: 'Email' });
      recommendations.push({ priority: 'media', text: 'Configurare DKIM per autenticare le email in uscita', category: 'Email' });
    }
    if (safeGet(r, 'mailConfig.data.spf.exists', false) && safeGet(r, 'mailConfig.data.dmarc.exists', false) && safeGet(r, 'mailConfig.data.dkim.exists', false)) {
      positives.push({ text: 'Configurazione email completa (SPF, DMARC, DKIM)', category: 'Email' });
    }
    
    // HSTS
    if (!safeGet(r, 'hsts.data.enabled', false)) {
      issues.push({ severity: 'warning', text: 'HSTS non abilitato', category: 'HTTPS' });
      recommendations.push({ priority: 'media', text: 'Abilitare HSTS per forzare connessioni HTTPS sicure', category: 'HTTPS' });
    } else {
      positives.push({ text: 'HSTS abilitato correttamente', category: 'HTTPS' });
      if (!safeGet(r, 'hsts.data.preload', false)) {
        recommendations.push({ priority: 'bassa', text: 'Considerare l\'aggiunta del sito alla lista HSTS Preload', category: 'HTTPS' });
      }
    }

    // Robots.txt & Sitemap
    if (!safeGet(r, 'robotsTxt.data.exists', false)) {
      issues.push({ severity: 'info', text: 'File robots.txt non trovato', category: 'SEO' });
      recommendations.push({ priority: 'bassa', text: 'Creare un file robots.txt per gestire l\'indicizzazione', category: 'SEO' });
    }
    if (!safeGet(r, 'sitemap.data.exists', false)) {
      issues.push({ severity: 'info', text: 'Sitemap non trovata', category: 'SEO' });
      recommendations.push({ priority: 'bassa', text: 'Creare una sitemap XML per migliorare l\'indicizzazione', category: 'SEO' });
    }
    
    // Firewall
    if (safeGet(r, 'firewall.data.detected', false)) {
      positives.push({ text: `WAF rilevato: ${safeGet(r, 'firewall.data.type', 'Unknown')}`, category: 'Sicurezza' });
    } else {
      recommendations.push({ priority: 'media', text: 'Considerare l\'implementazione di un Web Application Firewall (WAF)', category: 'Sicurezza' });
    }

    // Performance - only analyze if Lighthouse data is available
    const lighthouseAvailable = safeGet(r, 'quality.data.lighthouse.available', false);
    if (lighthouseAvailable) {
      const perfScore = safeGet(r, 'quality.data.lighthouse.performance', 0);
      if (perfScore < 50) {
        issues.push({ severity: 'warning', text: `Performance bassa: ${perfScore}/100`, category: 'Performance' });
        recommendations.push({ priority: 'media', text: 'Ottimizzare le performance del sito (compressione, caching, minificazione)', category: 'Performance' });
      } else if (perfScore >= 80) {
        positives.push({ text: `Ottime performance: ${perfScore}/100`, category: 'Performance' });
      }
    }
    
    // TLS
    const tlsVersions = safeArr(safeGet(r, 'tlsCipher.data.versions', []));
    if (tlsVersions.find(v => v?.version === 'TLS 1.0' && v?.supported)) {
      issues.push({ severity: 'critical', text: 'TLS 1.0 ancora supportato (deprecato)', category: 'TLS' });
      recommendations.push({ priority: 'alta', text: 'Disabilitare TLS 1.0 e 1.1, mantenere solo TLS 1.2 e 1.3', category: 'TLS' });
    }
    if (tlsVersions.find(v => v?.version === 'TLS 1.3' && v?.supported)) {
      positives.push({ text: 'TLS 1.3 supportato', category: 'TLS' });
    }

    // Security.txt
    if (!safeGet(r, 'securityTxt.data.exists', false)) {
      recommendations.push({ priority: 'bassa', text: 'Aggiungere un file security.txt per facilitare la segnalazione di vulnerabilità', category: 'Sicurezza' });
    } else {
      positives.push({ text: 'Security.txt presente', category: 'Sicurezza' });
    }
    
    // Calculate score - SAME FORMULA AS AnalysisSummary
    const criticalCount = issues.filter(i => i.severity === 'critical').length;
    const warningCount = issues.filter(i => i.severity === 'warning').length;
    const infoCount = issues.filter(i => i.severity === 'info').length;
    let score = 100 - (criticalCount * 15) - (warningCount * 5) - (infoCount * 2);
    score = Math.max(0, Math.min(100, score));
    
    const getScoreLabel = (s) => s >= 80 ? 'Buono' : s >= 60 ? 'Discreto' : 'Da Migliorare';
    
    const content = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Smart Web Check - ${results.domain}</title>
<style>
*{box-sizing:border-box}
body{font-family:'Segoe UI',Arial,sans-serif;padding:10px 25px;background:#fff;color:#333;font-size:16px;line-height:1.7;margin:0;max-width:100%;overflow-x:hidden}
h1{color:#DC3545;margin-bottom:5px;font-size:32px;border-bottom:3px solid #DC3545;padding-bottom:10px}
h2{color:#DC3545;font-size:22px;margin-top:20px;margin-bottom:10px;background:#f8f9fa;padding:12px;border-left:4px solid #DC3545}
h3{color:#333;font-size:18px;margin-top:15px;margin-bottom:8px}
.header{margin-bottom:20px}
.domain{font-size:24px;color:#333;font-weight:bold}
.date{color:#666;font-size:14px}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:15px;margin-bottom:20px;max-width:100%}
.section{margin-bottom:15px;page-break-inside:avoid;background:#f8f9fa;padding:15px;border-radius:5px;overflow:hidden;max-width:100%}
.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee;font-size:15px;flex-wrap:wrap;gap:5px}
.row:last-child{border-bottom:none}
.label{color:#666;font-weight:500;flex-shrink:0;max-width:45%}
.value{color:#333;text-align:right;max-width:55%;word-break:break-all;overflow-wrap:break-word;hyphens:auto}
.badge{display:inline-block;padding:4px 12px;border-radius:3px;font-size:13px;font-weight:bold;margin-left:5px}
.success{background:#d4edda;color:#155724}
.warning{background:#fff3cd;color:#856404}
.error{background:#f8d7da;color:#721c24}
.info{background:#d1ecf1;color:#0c5460}
.score-box{text-align:center;padding:20px;background:linear-gradient(135deg,#1a1a2e,#16162a);color:#fff;border-radius:10px;margin-bottom:15px}
.score-number{font-size:56px;font-weight:bold}
.score-label{font-size:16px;opacity:0.8}
.analysis-section{background:#fff;border:2px solid #DC3545;border-radius:8px;padding:25px;margin-top:20px;page-break-before:always}
.issue-item{padding:12px;margin:10px 0;border-radius:4px;font-size:15px}
.issue-critical{background:#f8d7da;border-left:4px solid #dc3545}
.issue-warning{background:#fff3cd;border-left:4px solid #ffc107}
.rec-item{padding:12px;margin:10px 0;background:#e8f4f8;border-left:4px solid #17a2b8;border-radius:4px;font-size:15px}
.positive-item{padding:12px;margin:10px 0;background:#d4edda;border-left:4px solid #28a745;border-radius:4px;font-size:15px}
.footer{margin-top:30px;text-align:center;color:#999;font-size:13px;border-top:2px solid #DC3545;padding-top:15px}
.logo-header{display:flex;align-items:center;margin:0;padding:0;gap:10px}
.logo-img{height:300px;width:auto;flex-shrink:0}
.map-container{width:100%;height:200px;border-radius:8px;overflow:hidden;margin:10px 0}
.map-img{width:100%;height:100%;object-fit:cover}
.screenshot-container{width:100%;max-height:300px;border-radius:8px;overflow:hidden;margin:10px 0;border:1px solid #ddd}
.screenshot-img{width:100%;height:auto;object-fit:contain}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.section{break-inside:avoid}.analysis-section{page-break-before:always}}
</style></head><body>

<div class="logo-header">
<img src="https://customer-assets.emergentagent.com/job_web-audit-tool-7/artifacts/o8y9x9lm_Logo%20Smart%20Info%20CYBER%20Red.png" alt="Smart Info Cyber Security" class="logo-img" />
<div style="margin-left:5px">
<div style="color:#DC3545;font-size:18px;font-weight:bold;margin:0">Security Report</div>
<div style="font-size:10px;color:#666">Report: ${new Date(results.analyzedAt).toLocaleString('it-IT')}</div>
</div>
<div style="flex-grow:1;text-align:center">
<div style="display:inline-flex;align-items:center;gap:12px;background:#f8f9fa;padding:8px 20px;border-radius:8px;border:2px solid #DC3545">
<img src="https://www.google.com/s2/favicons?domain=${results.domain}&sz=64" alt="favicon" style="width:40px;height:40px;border-radius:6px" onerror="this.style.display='none'" />
<div style="font-size:24px;color:#333;font-weight:bold">${results.domain}</div>
</div>
</div>
</div>

<div class="score-box" style="margin-top:8px">
<div class="score-number" style="color:${score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444'}">${score}/100</div>
<div class="score-label">Punteggio Sicurezza Complessivo</div>
</div>

<!-- Server Location Map -->
${(safeGet(r, 'serverLocation.data.lat', 0) !== 0) ? `
<div class="section" style="margin-bottom:15px">
<h3>🗺️ Posizione Server</h3>
<div class="map-container">
<img class="map-img" src="https://static-maps.yandex.ru/1.x/?lang=en_US&ll=${safeGet(r, 'serverLocation.data.lng', 0)},${safeGet(r, 'serverLocation.data.lat', 0)}&z=4&l=map&size=650,200&pt=${safeGet(r, 'serverLocation.data.lng', 0)},${safeGet(r, 'serverLocation.data.lat', 0)},pm2rdm" alt="Server Location Map" onerror="this.style.display='none'" />
</div>
<div style="text-align:center;font-size:14px;color:#666">${safeGet(r, 'serverLocation.data.city')}, ${safeGet(r, 'serverLocation.data.region')}, ${safeGet(r, 'serverLocation.data.country')} - ${safeGet(r, 'ipAddress.data.org')}</div>
</div>
` : ''}

<div class="grid">
<div class="section">
<h3>📍 Informazioni Server</h3>
<div class="row"><span class="label">IP Address:</span><span class="value">${safeGet(r, 'ipAddress.data.ip')}</span></div>
<div class="row"><span class="label">Location:</span><span class="value">${safeGet(r, 'serverLocation.data.city')}, ${safeGet(r, 'serverLocation.data.country')}</span></div>
<div class="row"><span class="label">ISP:</span><span class="value">${safeGet(r, 'ipAddress.data.isp')}</span></div>
<div class="row"><span class="label">ASN:</span><span class="value">${safeGet(r, 'ipAddress.data.asn')}</span></div>
<div class="row"><span class="label">Datacenter:</span><span class="value">${safeGet(r, 'serverLocation.data.datacenter')}</span></div>
</div>

<div class="section">
<h3>🔐 Certificato SSL</h3>
<div class="row"><span class="label">Emittente:</span><span class="value">${safeGet(r, 'sslCertificate.data.issuer')}</span></div>
<div class="row"><span class="label">Valido dal:</span><span class="value">${safeGet(r, 'sslCertificate.data.validFrom')}</span></div>
<div class="row"><span class="label">Scadenza:</span><span class="value">${safeGet(r, 'sslCertificate.data.validTo')}</span></div>
<div class="row"><span class="label">Giorni rimanenti:</span><span class="value">${sslDays}<span class="badge ${sslDays > 30 ? 'success' : 'warning'}">${sslDays > 30 ? 'OK' : 'Rinnova'}</span></span></div>
<div class="row"><span class="label">Protocollo:</span><span class="value">${safeGet(r, 'sslCertificate.data.protocol')}</span></div>
<div class="row"><span class="label">Grado:</span><span class="value"><span class="badge success">${sslGrade}</span></span></div>
<div class="row"><span class="label">OCSP Status:</span><span class="value"><span class="badge ${ocspStatus === 'good' ? 'success' : ocspStatus === 'revoked' ? 'error' : 'info'}">${ocspStatus === 'good' ? 'Valido ✓' : ocspStatus === 'revoked' ? 'REVOCATO ✗' : 'Non verificato'}</span></span></div>
</div>
</div>

<!-- Website Screenshot -->
${safeGet(r, 'screenshot.data.image', '') ? `
<div class="section" style="margin-bottom:15px">
<h3>📸 Screenshot Sito Web</h3>
<div class="screenshot-container">
<img class="screenshot-img" src="${safeGet(r, 'screenshot.data.image', '')}" alt="Website Screenshot" onerror="this.parentElement.style.display='none'" />
</div>
</div>
` : ''}

<div class="grid">
<div class="section">
<h3>📋 DNS Records</h3>
<div class="row"><span class="label">A Records:</span><span class="value">${safeArr(safeGet(r, 'dnsRecords.data.a', [])).join(', ') || 'N/A'}</span></div>
<div class="row"><span class="label">MX Records:</span><span class="value">${safeArr(safeGet(r, 'dnsRecords.data.mx', [])).join(', ') || 'N/A'}</span></div>
<div class="row"><span class="label">NS Records:</span><span class="value">${safeArr(safeGet(r, 'dnsRecords.data.ns', [])).join(', ') || 'N/A'}</span></div>
<div class="row"><span class="label">DNSSEC:</span><span class="value"><span class="badge ${safeGet(r, 'dnssec.data.enabled', false) ? 'success' : 'warning'}">${safeGet(r, 'dnssec.data.enabled', false) ? 'Abilitato' : 'Non configurato'}</span></span></div>
</div>

<div class="section">
<h3>🛡️ Security Headers</h3>
<div class="row"><span class="label">Score:</span><span class="value"><span class="badge ${safeGet(r, 'securityHeaders.data.scoreNumeric', 0) >= 70 ? 'success' : 'warning'}">${safeGet(r, 'securityHeaders.data.score')}</span></span></div>
${secHeaders.map(h => `<div class="row"><span class="label">${h?.name || 'Header'}:</span><span class="value"><span class="badge ${h?.present ? 'success' : 'error'}">${h?.present ? '✓' : '✗'}</span></span></div>`).join('')}
</div>
</div>

<div class="grid">
<div class="section">
<h3>📧 Email Security</h3>
<div class="row"><span class="label">SPF:</span><span class="value"><span class="badge ${safeGet(r, 'mailConfig.data.spf.exists', false) ? 'success' : 'error'}">${safeGet(r, 'mailConfig.data.spf.exists', false) ? 'Configurato' : 'Mancante'}</span></span></div>
<div class="row"><span class="label">DMARC:</span><span class="value"><span class="badge ${safeGet(r, 'mailConfig.data.dmarc.exists', false) ? 'success' : 'error'}">${safeGet(r, 'mailConfig.data.dmarc.exists', false) ? 'Configurato' : 'Mancante'}</span></span></div>
<div class="row"><span class="label">DKIM:</span><span class="value"><span class="badge ${safeGet(r, 'mailConfig.data.dkim.exists', false) ? 'success' : 'error'}">${safeGet(r, 'mailConfig.data.dkim.exists', false) ? 'Configurato' : 'Mancante'}</span></span></div>
</div>

<div class="section">
<h3>🔒 TLS Configuration</h3>
${tlsVersions.map(v => `<div class="row"><span class="label">${v?.version || 'TLS'}:</span><span class="value"><span class="badge ${v?.supported ? ((v?.version || '').includes('1.0') || (v?.version || '').includes('1.1') ? 'warning' : 'success') : 'info'}">${v?.supported ? 'Supportato' : 'Non supportato'}</span></span></div>`).join('')}
<div class="row"><span class="label">Forward Secrecy:</span><span class="value"><span class="badge ${safeGet(r, 'tlsCipher.data.forwardSecrecy', false) ? 'success' : 'warning'}">${safeGet(r, 'tlsCipher.data.forwardSecrecy', false) ? 'Sì' : 'No'}</span></span></div>
</div>
</div>

<div class="grid">
<div class="section">
<h3>🔥 Firewall / WAF</h3>
<div class="row"><span class="label">Rilevato:</span><span class="value"><span class="badge ${safeGet(r, 'firewall.data.detected', false) ? 'success' : 'warning'}">${safeGet(r, 'firewall.data.detected', false) ? 'Sì' : 'No'}</span></span></div>
${safeGet(r, 'firewall.data.detected', false) ? `<div class="row"><span class="label">Tipo:</span><span class="value">${safeGet(r, 'firewall.data.type')}</span></div>` : ''}
</div>

<div class="section">
<h3>🌐 HSTS</h3>
<div class="row"><span class="label">Abilitato:</span><span class="value"><span class="badge ${safeGet(r, 'hsts.data.enabled', false) ? 'success' : 'warning'}">${safeGet(r, 'hsts.data.enabled', false) ? 'Sì' : 'No'}</span></span></div>
${safeGet(r, 'hsts.data.enabled', false) ? `
<div class="row"><span class="label">Max Age:</span><span class="value">${safeGet(r, 'hsts.data.maxAge', 0)}s</span></div>
<div class="row"><span class="label">Include Subdomains:</span><span class="value">${safeGet(r, 'hsts.data.includeSubDomains', false) ? 'Sì' : 'No'}</span></div>
<div class="row"><span class="label">Preload:</span><span class="value">${safeGet(r, 'hsts.data.preload', false) ? 'Sì' : 'No'}</span></div>
` : ''}
</div>
</div>

<div class="grid">
<div class="section">
<h3>📊 Performance</h3>
${safeGet(r, 'quality.data.lighthouse.available', false) ? `
<div class="row"><span class="label">Performance:</span><span class="value">${safeGet(r, 'quality.data.lighthouse.performance', 0)}/100</span></div>
<div class="row"><span class="label">Accessibilità:</span><span class="value">${safeGet(r, 'quality.data.lighthouse.accessibility', 0)}/100</span></div>
<div class="row"><span class="label">Best Practices:</span><span class="value">${safeGet(r, 'quality.data.lighthouse.bestPractices', 0)}/100</span></div>
<div class="row"><span class="label">SEO:</span><span class="value">${safeGet(r, 'quality.data.lighthouse.seo', 0)}/100</span></div>
` : `<div class="row"><span class="label">Lighthouse:</span><span class="value">Non disponibile (richiede Google API)</span></div>`}
<div class="row"><span class="label">Tempo di caricamento:</span><span class="value">${safeGet(r, 'quality.data.loadTime')}</span></div>
</div>

<div class="section">
<h3>🚪 Open Ports</h3>
${safeArr(safeGet(r, 'openPorts.data.ports', [])).map(p => `<div class="row"><span class="label">Porta ${p?.port || '?'} (${p?.service || 'unknown'}):</span><span class="value"><span class="badge ${p?.status === 'open' ? 'success' : p?.status === 'filtered' ? 'warning' : 'info'}">${p?.status || 'unknown'}</span></span></div>`).join('') || '<div class="row"><span class="label">Nessuna porta</span></div>'}
</div>
</div>

<div class="section">
<h3>🏢 Whois Information</h3>
<div class="row"><span class="label">Registrar:</span><span class="value">${safeGet(r, 'whois.data.registrar')}</span></div>
<div class="row"><span class="label">Data creazione:</span><span class="value">${safeGet(r, 'whois.data.createdDate')}</span></div>
<div class="row"><span class="label">Data scadenza:</span><span class="value">${safeGet(r, 'whois.data.expiryDate')}</span></div>
<div class="row"><span class="label">Organizzazione:</span><span class="value">${safeGet(r, 'whois.data.registrantOrg')}</span></div>
</div>

<div class="grid">
<div class="section">
<h3>🍪 Cookies</h3>
<div class="row"><span class="label">Totale:</span><span class="value">${safeGet(r, 'cookies.data.total', 0)}</span></div>
<div class="row"><span class="label">Secure:</span><span class="value">${safeGet(r, 'cookies.data.secure', 0)}</span></div>
<div class="row"><span class="label">HttpOnly:</span><span class="value">${safeGet(r, 'cookies.data.httpOnly', 0)}</span></div>
<div class="row"><span class="label">SameSite:</span><span class="value">${safeGet(r, 'cookies.data.sameSite', 0)}</span></div>
<div class="row"><span class="label">Third-Party:</span><span class="value">${safeGet(r, 'cookies.data.thirdParty', 0)}</span></div>
</div>

<div class="section">
<h3>🌍 Server Status</h3>
<div class="row"><span class="label">Online:</span><span class="value"><span class="badge ${safeGet(r, 'uptimeStatus.data.isUp', true) ? 'success' : 'error'}">${safeGet(r, 'uptimeStatus.data.isUp', true) ? 'Sì' : 'No'}</span></span></div>
<div class="row"><span class="label">Status Code:</span><span class="value">${safeGet(r, 'uptimeStatus.data.statusCode', 200)}</span></div>
<div class="row"><span class="label">Response Time:</span><span class="value">${safeGet(r, 'uptimeStatus.data.responseTime')}</span></div>
<div class="row"><span class="label">Uptime 30 giorni:</span><span class="value">${safeGet(r, 'uptimeStatus.data.uptime30Days', '99.9%')}</span></div>
</div>
</div>

<div class="grid">
<div class="section">
<h3>💻 Tech Stack</h3>
${safeArr(safeGet(r, 'techStack.data.technologies', [])).slice(0, 8).map(t => `<div class="row"><span class="label">${t?.name || 'Technology'}:</span><span class="value">${t?.category || 'Other'}</span></div>`).join('') || '<div class="row"><span class="label">Nessuna tecnologia rilevata</span></div>'}
</div>

<div class="section">
<h3>🔗 Redirect Chain</h3>
<div class="row"><span class="label">Redirect totali:</span><span class="value">${safeGet(r, 'redirectChain.data.totalRedirects', 0)}</span></div>
<div class="row"><span class="label">HTTPS Redirect:</span><span class="value"><span class="badge ${safeGet(r, 'redirects.data.httpsRedirect', true) ? 'success' : 'warning'}">${safeGet(r, 'redirects.data.httpsRedirect', true) ? 'Sì' : 'No'}</span></span></div>
<div class="row"><span class="label">URL Finale:</span><span class="value" style="font-size:12px;word-break:break-all">${safeGet(r, 'redirectChain.data.finalUrl', 'https://' + results.domain)}</span></div>
</div>
</div>

<div class="grid">
<div class="section">
<h3>📈 Global Ranking</h3>
<div class="row"><span class="label">Rank Globale:</span><span class="value">${safeGet(r, 'globalRank.data.global', 'N/A')}</span></div>
<div class="row"><span class="label">Paese:</span><span class="value">${safeGet(r, 'globalRank.data.country', 'N/A')}</span></div>
<div class="row"><span class="label">Rank Paese:</span><span class="value">${safeGet(r, 'globalRank.data.countryRank', 'N/A')}</span></div>
<div class="row"><span class="label">Categoria:</span><span class="value">${safeGet(r, 'globalRank.data.category', 'N/A')}</span></div>
</div>

<div class="section">
<h3>🌱 Carbon Footprint</h3>
<div class="row"><span class="label">CO2 per visita:</span><span class="value">${safeGet(r, 'carbonFootprint.data.co2PerView', 'N/A')}</span></div>
<div class="row"><span class="label">Più pulito del:</span><span class="value">${safeGet(r, 'carbonFootprint.data.cleanerThan', 'N/A')}</span></div>
<div class="row"><span class="label">Hosting Green:</span><span class="value"><span class="badge ${safeGet(r, 'carbonFootprint.data.greenHosting', false) ? 'success' : 'warning'}">${safeGet(r, 'carbonFootprint.data.greenHosting', false) ? 'Sì' : 'No'}</span></span></div>
<div class="row"><span class="label">Score Sostenibilità:</span><span class="value">${safeGet(r, 'carbonFootprint.data.sustainabilityScore', 'C')}</span></div>
</div>
</div>

<div class="grid">
<div class="section">
<h3>⚠️ Threats & Blocklist</h3>
<div class="row"><span class="label">Sito Sicuro:</span><span class="value"><span class="badge ${safeGet(r, 'threats.data.safe', true) ? 'success' : 'error'}">${safeGet(r, 'threats.data.safe', true) ? 'Sì' : 'No'}</span></span></div>
<div class="row"><span class="label">Google Safe Browsing:</span><span class="value"><span class="badge success">${safeGet(r, 'threats.data.googleSafeBrowsing', 'Clean')}</span></span></div>
<div class="row"><span class="label">VirusTotal - Clean:</span><span class="value">${safeGet(r, 'threats.data.virusTotal.clean', 'N/A')}</span></div>
<div class="row"><span class="label">VirusTotal - Malicious:</span><span class="value">${safeGet(r, 'threats.data.virusTotal.malicious', 0)}</span></div>
</div>

<div class="section">
<h3>📚 Archive History</h3>
<div class="row"><span class="label">Disponibile:</span><span class="value"><span class="badge ${safeGet(r, 'archiveHistory.data.available', false) ? 'success' : 'warning'}">${safeGet(r, 'archiveHistory.data.available', false) ? 'Sì' : 'No'}</span></span></div>
<div class="row"><span class="label">Prima cattura:</span><span class="value">${safeGet(r, 'archiveHistory.data.firstCapture', 'N/A')}</span></div>
<div class="row"><span class="label">Ultima cattura:</span><span class="value">${safeGet(r, 'archiveHistory.data.lastCapture', 'N/A')}</span></div>
<div class="row"><span class="label">Catture totali:</span><span class="value">${safeGet(r, 'archiveHistory.data.totalCaptures', 0)}</span></div>
</div>
</div>

<div class="grid">
<div class="section">
<h3>🤖 Robots.txt</h3>
<div class="row"><span class="label">Presente:</span><span class="value"><span class="badge ${safeGet(r, 'robotsTxt.data.exists', false) ? 'success' : 'warning'}">${safeGet(r, 'robotsTxt.data.exists', false) ? 'Sì' : 'No'}</span></span></div>
</div>

<div class="section">
<h3>🗺️ Sitemap</h3>
<div class="row"><span class="label">Presente:</span><span class="value"><span class="badge ${safeGet(r, 'sitemap.data.exists', false) ? 'success' : 'warning'}">${safeGet(r, 'sitemap.data.exists', false) ? 'Sì' : 'No'}</span></span></div>
<div class="row"><span class="label">URL:</span><span class="value" style="font-size:12px">${safeGet(r, 'sitemap.data.url', 'N/A')}</span></div>
<div class="row"><span class="label">Entries:</span><span class="value">${safeGet(r, 'sitemap.data.entries', 0)}</span></div>
</div>
</div>

<div class="grid">
<div class="section">
<h3>📱 Social Tags</h3>
<div class="row"><span class="label">Title:</span><span class="value" style="font-size:13px">${safeGet(r, 'socialTags.data.title', 'N/A')}</span></div>
<div class="row"><span class="label">Description:</span><span class="value" style="font-size:12px;max-width:70%">${String(safeGet(r, 'socialTags.data.description', 'N/A')).substring(0, 100)}...</span></div>
</div>

<div class="section">
<h3>🔐 Security.txt</h3>
<div class="row"><span class="label">Presente:</span><span class="value"><span class="badge ${safeGet(r, 'securityTxt.data.exists', false) ? 'success' : 'warning'}">${safeGet(r, 'securityTxt.data.exists', false) ? 'Sì' : 'No'}</span></span></div>
<div class="row"><span class="label">Contact:</span><span class="value">${safeGet(r, 'securityTxt.data.contact', 'N/A')}</span></div>
<div class="row"><span class="label">Expires:</span><span class="value">${safeGet(r, 'securityTxt.data.expires', 'N/A')}</span></div>
</div>
</div>

<div class="grid">
<div class="section">
<h3>🔗 Linked Pages</h3>
<div class="row"><span class="label">Link Interni:</span><span class="value">${safeGet(r, 'linkedPages.data.internal.count', 0)}</span></div>
<div class="row"><span class="label">Link Esterni:</span><span class="value">${safeGet(r, 'linkedPages.data.external.count', 0)}</span></div>
<div class="row"><span class="label">Link Rotti:</span><span class="value">${safeGet(r, 'linkedPages.data.broken.count', 0)}</span></div>
</div>

<div class="section">
<h3>🌐 HTTP Headers</h3>
<div class="row"><span class="label">Server:</span><span class="value">${safeGet(r, 'headers.data.server', 'N/A')}</span></div>
<div class="row"><span class="label">Content-Type:</span><span class="value">${safeGet(r, 'headers.data.contentType', 'N/A')}</span></div>
<div class="row"><span class="label">Cache-Control:</span><span class="value">${safeGet(r, 'headers.data.cacheControl', 'N/A')}</span></div>
</div>
</div>

<div class="grid">
<div class="section">
<h3>🌐 DNS Server</h3>
${safeArr(safeGet(r, 'dnsServer.data.servers', [])).slice(0, 3).map(s => {
  const provider = String(s?.provider || 'DNS Server').substring(0, 25);
  const ip = String(s?.ip || 'N/A');
  return `<div class="row"><span class="label">${provider}:</span><span class="value" style="font-size:13px">${ip}</span></div>`;
}).join('') || '<div class="row"><span class="label">N/A</span></div>'}
<div class="row"><span class="label">Response Time:</span><span class="value">${safeGet(r, 'dnsServer.data.responseTime', 'N/A')}</span></div>
<div class="row"><span class="label">DNSSEC:</span><span class="value"><span class="badge ${safeGet(r, 'dnsServer.data.dnssec', false) ? 'success' : 'warning'}">${safeGet(r, 'dnsServer.data.dnssec', false) ? 'Abilitato' : 'No'}</span></span></div>
</div>

<div class="section">
<h3>🛣️ Traceroute</h3>
<div class="row"><span class="label">Hops Totali:</span><span class="value">${safeGet(r, 'traceRoute.data.totalHops', 0)}</span></div>
<div class="row"><span class="label">Latenza Media:</span><span class="value">${safeGet(r, 'traceRoute.data.avgLatency', 'N/A')}</span></div>
${safeArr(safeGet(r, 'traceRoute.data.hops', [])).slice(0, 3).map(h => `<div class="row"><span class="label">Hop ${h?.hop || '?'}:</span><span class="value" style="font-size:13px">${String(h?.ip || 'N/A').substring(0,20)} ${h?.time || ''}</span></div>`).join('')}
</div>
</div>

<div class="analysis-section">
<h2>🔍 ANALISI INTELLIGENTE & RACCOMANDAZIONI</h2>

<div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px">
<div style="text-align:center;padding:15px;background:#f8f9fa;border-radius:8px">
<div style="font-size:32px;font-weight:bold;color:${score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444'}">${score}</div>
<div style="font-size:12px;color:#666">Punteggio</div>
<div style="font-size:11px;color:${score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444'}">${getScoreLabel(score)}</div>
</div>
<div style="text-align:center;padding:15px;background:#f8f9fa;border-radius:8px">
<div style="font-size:32px;font-weight:bold;color:#dc3545">${criticalCount}</div>
<div style="font-size:12px;color:#666">Problemi Critici</div>
</div>
<div style="text-align:center;padding:15px;background:#f8f9fa;border-radius:8px">
<div style="font-size:32px;font-weight:bold;color:#f59e0b">${warningCount}</div>
<div style="font-size:12px;color:#666">Avvisi</div>
</div>
<div style="text-align:center;padding:15px;background:#f8f9fa;border-radius:8px">
<div style="font-size:32px;font-weight:bold;color:#10b981">${positives.length}</div>
<div style="font-size:12px;color:#666">Punti Positivi</div>
</div>
</div>

<h3>❌ Problemi Rilevati (${issues.length})</h3>
${issues.length > 0 ? issues
  .sort((a, b) => {
    // Sort by severity: critical first, then warning, then info
    const severityOrder = { critical: 0, warning: 1, info: 2 };
    return (severityOrder[a.severity] || 3) - (severityOrder[b.severity] || 3);
  })
  .map(i => `<div class="issue-item issue-${i.severity === 'critical' ? 'critical' : 'warning'}"><strong>[${i.severity === 'critical' ? 'CRITICO' : i.severity === 'warning' ? 'Avviso' : 'Info'}]</strong> ${i.text} <span style="color:#999;font-size:12px">[${i.category}]</span></div>`).join('') : '<p>Nessun problema critico rilevato.</p>'}

<h3>💡 Raccomandazioni (${recommendations.length})</h3>
${recommendations.length > 0 ? recommendations.sort((a, b) => {
  const order = { alta: 0, media: 1, bassa: 2 };
  return order[a.priority] - order[b.priority];
}).map(rec => `<div class="rec-item"><strong>[Priorità ${rec.priority}]</strong> ${rec.text} <span style="color:#999;font-size:12px">[${rec.category}]</span></div>`).join('') : '<p>Nessuna raccomandazione al momento.</p>'}

<h3>✅ Aspetti Positivi (${positives.length})</h3>
${positives.length > 0 ? positives.map(p => `<div class="positive-item">✓ ${p.text} <span style="color:#999;font-size:12px">[${p.category}]</span></div>`).join('') : ''}
</div>

<div class="footer">
<strong>Smart Web Check - Security Report</strong><br>
Report generato il ${new Date().toLocaleString('it-IT')}<br>
© ${new Date().getFullYear()} Smart Info Cyber Security - Tutti i diritti riservati
</div>

</body></html>`;
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();
      printWindow.onload = function() { printWindow.print(); };
    }
  };

  useEffect(() => {
    const analyzeWebsite = async () => {
      setLoading(true);
      setProgress(0);
      
      // Step 1: Check if all services are healthy
      setProgress(5);
      const healthStatus = await checkServicesHealth();
      
      if (!healthStatus.all_services_ok) {
        // Services are not healthy - show error
        console.error('Service health check failed:', healthStatus);
        setResults({
          domain: decodeURIComponent(domain),
          analyzedAt: new Date().toISOString(),
          serviceError: true,
          healthStatus: healthStatus,
          results: {}
        });
        setLoading(false);
        return;
      }
      
      setProgress(10);

      try {
        const decodedDomain = decodeURIComponent(domain);
        // Use the new two-phase loading with progress callback
        const rawData = await fetchRealAnalysis(decodedDomain, (progressPercent) => {
          // Map 0-100 from API to 10-100 for display (10% already used for health check)
          setProgress(10 + Math.floor(progressPercent * 0.9));
        });
        const formattedData = transformToDisplayFormat(decodedDomain, rawData);
        setResults(formattedData);
        setProgress(100);
      } catch (error) {
        console.error('Analysis error:', error);
        // Fallback to show error state
        setResults({
          domain: decodeURIComponent(domain),
          analyzedAt: new Date().toISOString(),
          error: error.message,
          results: {}
        });
      } finally {
        setLoading(false);
      }
    };

    analyzeWebsite();
  }, [domain]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f1a]">
        <Header hideNavButtons={true} />
        <main className="pt-36 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Loader2 className="h-12 w-12 text-cyan-400 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-2">Analyzing {decodeURIComponent(domain)}</h2>
            <div className="max-w-md mx-auto mt-6">
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
              </div>
              <p className="text-gray-500 text-sm mt-2">{Math.round(Math.min(progress, 100))}%</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Service health check error
  if (results?.serviceError) {
    const health = results.healthStatus || {};
    return (
      <div className="min-h-screen bg-[#0f0f1a]">
        <Header hideNavButtons={true} />
        <main className="pt-36 pb-16 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-8 text-center">
              <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">⚠️ Connessione Non Riuscita</h2>
              <p className="text-gray-300 mb-6">
                {health.message?.includes('Timeout') || health.message?.includes('timeout') 
                  ? 'La connessione è troppo lenta. Verifica la tua connessione internet e riprova.'
                  : 'Impossibile connettersi ai servizi di analisi. Riprova tra qualche secondo.'}
              </p>
              
              {/* Service Status List */}
              <div className="bg-black/30 rounded-lg p-4 mb-6 text-left">
                <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase">Stato dei Servizi</h3>
                <div className="space-y-2">
                  {Object.entries(health.services || {}).map(([name, info]) => (
                    <div key={name} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                      <span className="text-white capitalize">{name}</span>
                      <div className="flex items-center gap-2">
                        {info.status === 'ok' ? (
                          <CheckCircle className="h-4 w-4 text-green-400" />
                        ) : info.status === 'warning' ? (
                          <AlertTriangle className="h-4 w-4 text-yellow-400" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-400" />
                        )}
                        <span className={`text-sm ${info.status === 'ok' ? 'text-green-400' : info.status === 'warning' ? 'text-yellow-400' : 'text-red-400'}`}>
                          {info.message}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-4 justify-center">
                <button 
                  onClick={() => window.location.reload()} 
                  className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-colors"
                >
                  🔄 Riprova
                </button>
                <Link 
                  to="/" 
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  ← Torna alla Home
                </Link>
              </div>
              
              <p className="text-gray-500 text-sm mt-6">
                Se il problema persiste, contatta l'amministratore del sistema.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Safety check for results
  if (!results || !results.results) {
    return (
      <div className="min-h-screen bg-[#0f0f1a]">
        <Header hideNavButtons={true} />
        <main className="pt-36 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-2">Errore nell'analisi</h2>
            <p className="text-gray-400">Si è verificato un errore durante l'analisi del dominio.</p>
            <Link to="/" className="mt-4 inline-block px-4 py-2 bg-red-600 text-white rounded-lg">
              Torna alla Home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const r = results.results;
  
  // Get coordinates - fallback to 0,0 if not available
  const lat = r.serverLocation?.data?.lat || 0;
  const lng = r.serverLocation?.data?.lng || 0;

  // Performance chart data - with safe access
  const lighthouseAvailable = r.quality?.data?.lighthouse?.available;
  const performanceData = lighthouseAvailable ? [
    { name: 'Performance', value: r.quality?.data?.lighthouse?.performance || 0, fill: '#06b6d4' },
    { name: 'Accessibilità', value: r.quality?.data?.lighthouse?.accessibility || 0, fill: '#10b981' },
    { name: 'Best Practices', value: r.quality?.data?.lighthouse?.bestPractices || 0, fill: '#f59e0b' },
    { name: 'SEO', value: r.quality?.data?.lighthouse?.seo || 0, fill: '#8b5cf6' }
  ] : [];

  // Favicon URL
  const faviconUrl = `https://www.google.com/s2/favicons?domain=${results.domain}&sz=64`;

  // Safe data access helper
  const safe = (obj, defaultVal = 'N/A') => obj ?? defaultVal;
  const safeArr = (obj) => obj ?? [];

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <Header hideNavButtons={true} />
      <main className="pt-36 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header with Domain Info and Premium Banner */}
          <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Left: Domain Info */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <img 
                  src={faviconUrl} 
                  alt={`${results.domain} favicon`} 
                  className="h-10 w-10 rounded-lg bg-gray-800 p-1"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <h1 className="text-2xl font-bold text-white">{results.domain}</h1>
              </div>
              <p className="text-gray-400 text-sm flex items-center gap-2 mb-4">
                <Clock className="h-4 w-4" />
                {new Date(results.analyzedAt).toLocaleString('it-IT')}
              </p>
              <div className="flex gap-3 flex-wrap">
                <button onClick={handlePdfClick} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                  <Download className="h-4 w-4" /> Scarica PDF
                </button>
                <Link to="/about" className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                  <HelpCircle className="h-4 w-4" /> Guida
                </Link>
              </div>
            </div>

            {/* Right: Premium Services Banner */}
            <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-500/30 rounded-xl p-5">
              <h3 className="text-lg font-bold text-white mb-1">Vuoi un'analisi più approfondita?</h3>
              <p className="text-gray-300 text-sm mb-4">
                Scopri le vulnerabilità nascoste e proteggi la tua azienda con i nostri servizi premium
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowPremiumModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all flex items-center gap-2"
                >
                  <Shield className="h-4 w-4" /> Vulnerability Assessment
                </button>
                <button
                  onClick={() => setShowPremiumModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all flex items-center gap-2"
                >
                  <Search className="h-4 w-4" /> Cyber Investigation
                </button>
              </div>
            </div>
          </div>

          {/* Grid di card */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            
            {/* IP Info */}
            <AnalysisCard title="IP Info" icon={Globe}>
              <DataItem label="IP Address" value={safe(r.ipAddress?.data?.ip)} mono />
              <DataItem label="Type" value={safe(r.ipAddress?.data?.type)} />
              <DataItem label="Location" value={safe(r.ipAddress?.data?.location)} />
              <DataItem label="ISP" value={safe(r.ipAddress?.data?.isp)} />
              <DataItem label="ASN" value={safe(r.ipAddress?.data?.asn)} mono />
            </AnalysisCard>

            {/* SSL Chain */}
            <AnalysisCard title="SSL Chain" icon={Lock} status={
              r.sslCertificate?.data?.ocsp?.status === 'revoked' ? 'error' :
              (r.sslCertificate?.data?.daysRemaining || 0) > 30 ? 'success' : 'warning'
            }>
              <DataItem label="Issuer" value={safe(r.sslCertificate?.data?.issuer)} />
              <DataItem label="Valid From" value={safe(r.sslCertificate?.data?.validFrom)} />
              <DataItem label="Valid To" value={safe(r.sslCertificate?.data?.validTo)} />
              <DataItem label="Days Remaining" value={safe(r.sslCertificate?.data?.daysRemaining)} status={(r.sslCertificate?.data?.daysRemaining || 0) > 30 ? 'good' : 'warn'} />
              <DataItem label="Protocol" value={safe(r.sslCertificate?.data?.protocol)} />
              <DataItem 
                label="OCSP Status" 
                value={
                  r.sslCertificate?.data?.ocsp?.status === 'good' ? 'Valido ✓' :
                  r.sslCertificate?.data?.ocsp?.status === 'revoked' ? 'REVOCATO ✗' :
                  r.sslCertificate?.data?.ocsp?.checked ? 'Sconosciuto' : 'Non verificato'
                } 
                status={
                  r.sslCertificate?.data?.ocsp?.status === 'good' ? 'good' :
                  r.sslCertificate?.data?.ocsp?.status === 'revoked' ? 'bad' : undefined
                }
              />
              <div className="mt-3 flex gap-2 flex-wrap">
                <Tag color="green">{safe(r.sslCertificate?.data?.grade, 'N/A')}</Tag>
                <Tag color="cyan">{safe(r.sslCertificate?.data?.protocol, 'TLS')}</Tag>
                {r.sslCertificate?.data?.ocsp?.status === 'good' && <Tag color="green">OCSP OK</Tag>}
                {r.sslCertificate?.data?.ocsp?.status === 'revoked' && <Tag color="red">REVOCATO</Tag>}
              </div>
            </AnalysisCard>

            {/* DNS Records */}
            <AnalysisCard title="DNS Records" icon={Server}>
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-gray-500 mb-1">A Records</div>
                  <div className="flex flex-wrap gap-1">{safeArr(r.dnsRecords?.data?.a).slice(0,3).map((ip, i) => <Tag key={i} color="cyan">{ip}</Tag>)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">MX Records</div>
                  <div className="flex flex-wrap gap-1">{safeArr(r.dnsRecords?.data?.mx).slice(0,3).map((mx, i) => <Tag key={i} color="yellow">{String(mx).split('.')[0]}</Tag>)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">NS Records</div>
                  <div className="flex flex-wrap gap-1">{safeArr(r.dnsRecords?.data?.ns).slice(0,3).map((ns, i) => <Tag key={i} color="gray">{String(ns).split('.')[0]}</Tag>)}</div>
                </div>
              </div>
            </AnalysisCard>

            {/* Cookies */}
            <AnalysisCard title="Cookies" icon={Cookie}>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-[#0f0f1a] p-3 rounded text-center">
                  <div className="text-2xl font-bold text-white">{safe(r.cookies?.data?.total, 0)}</div>
                  <div className="text-xs text-gray-500">Totale</div>
                </div>
                <div className="bg-[#0f0f1a] p-3 rounded text-center">
                  <div className="text-2xl font-bold text-emerald-400">{safe(r.cookies?.data?.secure, 0)}</div>
                  <div className="text-xs text-gray-500">Secure</div>
                </div>
              </div>
              <DataItem label="HttpOnly" value={safe(r.cookies?.data?.httpOnly, 0)} status="good" />
              <DataItem label="SameSite" value={safe(r.cookies?.data?.sameSite, 0)} />
              <DataItem label="Third-Party" value={safe(r.cookies?.data?.thirdParty, 0)} status={(r.cookies?.data?.thirdParty || 0) > 0 ? 'warn' : 'good'} />
            </AnalysisCard>

            {/* Server Location with Dark Map */}
            <AnalysisCard title="Server Location" icon={MapPin} size="large">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <DataItem label="City" value={safe(r.serverLocation?.data?.city)} />
                  <DataItem label="Country" value={safe(r.serverLocation?.data?.country)} />
                  <DataItem label="Region" value={safe(r.serverLocation?.data?.region)} />
                  <DataItem label="Organization" value={safe(r.serverLocation?.data?.org)} />
                </div>
                <div className="h-[200px] rounded-lg overflow-hidden border border-gray-800">
                  <DarkWorldMap 
                    lat={lat} 
                    lng={lng} 
                    city={safe(r.serverLocation?.data?.city, 'Unknown')}
                    country={safe(r.serverLocation?.data?.country, 'Unknown')}
                    serverName={String(r.ipAddress?.data?.isp || 'Server').split(' ')[0].toUpperCase()}
                  />
                </div>
              </div>
            </AnalysisCard>

            {/* Quality Metrics with Chart */}
            <AnalysisCard title="Quality Metrics (Lighthouse)" icon={Gauge} size="large">
              {lighthouseAvailable ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-[180px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={performanceData} layout="vertical">
                        <XAxis type="number" domain={[0, 100]} hide />
                        <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {performanceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <DataItem label="Performance" value={`${r.quality?.data?.lighthouse?.performance || 0}/100`} status={(r.quality?.data?.lighthouse?.performance || 0) >= 80 ? 'good' : 'warn'} />
                    <DataItem label="Accessibilità" value={`${r.quality?.data?.lighthouse?.accessibility || 0}/100`} status={(r.quality?.data?.lighthouse?.accessibility || 0) >= 80 ? 'good' : 'warn'} />
                    <DataItem label="Best Practices" value={`${r.quality?.data?.lighthouse?.bestPractices || 0}/100`} status={(r.quality?.data?.lighthouse?.bestPractices || 0) >= 80 ? 'good' : 'warn'} />
                    <DataItem label="SEO" value={`${r.quality?.data?.lighthouse?.seo || 0}/100`} status={(r.quality?.data?.lighthouse?.seo || 0) >= 80 ? 'good' : 'warn'} />
                    <DataItem label="Load Time" value={safe(r.quality?.data?.loadTime)} />
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-gray-400 text-sm mb-2">Dati Lighthouse non disponibili</p>
                  <p className="text-gray-500 text-xs">Richiede configurazione Google Cloud API</p>
                  <div className="mt-4">
                    <DataItem label="Load Time" value={safe(r.quality?.data?.loadTime)} />
                  </div>
                </div>
              )}
            </AnalysisCard>

            {/* Security Headers */}
            <AnalysisCard title="HTTP Security Headers" icon={Shield} status={(r.securityHeaders?.data?.scoreNumeric || 0) >= 70 ? 'success' : 'warning'}>
              <div className="mb-3">
                <SecurityGauge score={r.securityHeaders?.data?.scoreNumeric || 50} label="Security Score" />
              </div>
              <div className="space-y-1">
                {safeArr(r.securityHeaders?.data?.headers).map((header, i) => (
                  <DataItem key={i} label={header.name} value={header.present ? 'Yes' : 'No'} status={header.present ? 'good' : 'bad'} />
                ))}
              </div>
            </AnalysisCard>

            {/* Headers */}
            <AnalysisCard title="HTTP Headers" icon={FileText}>
              <DataItem label="Server" value={safe(r.headers?.data?.server)} />
              <DataItem label="Content-Type" value={safe(r.headers?.data?.contentType)} />
              <DataItem label="X-Frame-Options" value={safe(r.headers?.data?.xFrameOptions)} status="good" />
              <DataItem label="X-Content-Type" value={safe(r.headers?.data?.xContentTypeOptions)} status="good" />
              <DataItem label="Cache-Control" value={safe(r.headers?.data?.cacheControl)} />
            </AnalysisCard>

            {/* Email Security */}
            <AnalysisCard title="Email Security" icon={Mail} status={r.mailConfig?.data?.spf?.exists && r.mailConfig?.data?.dmarc?.exists ? 'success' : 'warning'}>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-[#0f0f1a] rounded">
                  <span className="text-gray-400 text-xs">SPF</span>
                  <Tag color={r.mailConfig?.data?.spf?.exists ? 'green' : 'red'}>{r.mailConfig?.data?.spf?.exists ? 'Configured' : 'Missing'}</Tag>
                </div>
                <div className="flex items-center justify-between p-2 bg-[#0f0f1a] rounded">
                  <span className="text-gray-400 text-xs">DMARC</span>
                  <Tag color={r.mailConfig?.data?.dmarc?.exists ? 'green' : 'red'}>{r.mailConfig?.data?.dmarc?.exists ? 'Configured' : 'Missing'}</Tag>
                </div>
                <div className="flex items-center justify-between p-2 bg-[#0f0f1a] rounded">
                  <span className="text-gray-400 text-xs">DKIM</span>
                  <Tag color={r.mailConfig?.data?.dkim?.exists ? 'green' : 'red'}>{r.mailConfig?.data?.dkim?.exists ? 'Configured' : 'Missing'}</Tag>
                </div>
              </div>
            </AnalysisCard>

            {/* TLS Configuration */}
            <AnalysisCard title="TLS Cipher Suites" icon={Key}>
              <div className="space-y-2 mb-3">
                {safeArr(r.tlsCipher?.data?.versions).map((v, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-gray-400 text-xs">{v.version}</span>
                    <Tag color={v.supported ? (v.version?.includes('1.0') || v.version?.includes('1.1') ? 'yellow' : 'green') : 'gray'}>
                      {v.supported ? 'Supported' : 'Not Supported'}
                    </Tag>
                  </div>
                ))}
              </div>
              <DataItem label="Forward Secrecy" value={r.tlsCipher?.data?.forwardSecrecy ? 'Enabled' : 'Disabled'} status={r.tlsCipher?.data?.forwardSecrecy ? 'good' : 'bad'} />
            </AnalysisCard>

            {/* Open Ports */}
            <AnalysisCard title="Open Ports" icon={Network}>
              <div className="flex flex-wrap gap-2">
                {safeArr(r.openPorts?.data?.ports).map((port, i) => (
                  <PortBadge key={i} port={port.port} service={port.service} status={port.status || 'open'} />
                ))}
                {safeArr(r.openPorts?.data?.ports).length === 0 && <span className="text-gray-500 text-xs">Nessuna porta aperta rilevata</span>}
              </div>
            </AnalysisCard>

            {/* Firewall */}
            <AnalysisCard title="Firewall / WAF" icon={Flame} status={r.firewall?.data?.detected ? 'success' : 'warning'}>
              <DataItem label="WAF Detected" value={r.firewall?.data?.detected ? 'Yes' : 'No'} status={r.firewall?.data?.detected ? 'good' : 'warn'} />
              {r.firewall?.data?.detected && (
                <>
                  <DataItem label="Type" value={safe(r.firewall?.data?.type)} />
                  <DataItem label="Confidence" value={`${r.firewall?.data?.confidence || 0}%`} />
                </>
              )}
            </AnalysisCard>

            {/* HSTS */}
            <AnalysisCard title="HSTS" icon={ShieldCheck} status={r.hsts?.data?.enabled ? 'success' : 'warning'}>
              <DataItem label="HSTS Enabled" value={r.hsts?.data?.enabled ? 'Yes' : 'No'} status={r.hsts?.data?.enabled ? 'good' : 'bad'} />
              {r.hsts?.data?.enabled && (
                <>
                  <DataItem label="Max Age" value={`${r.hsts?.data?.maxAge || 0}s`} />
                  <DataItem label="Include Subdomains" value={r.hsts?.data?.includeSubDomains ? 'Yes' : 'No'} />
                  <DataItem label="Preload" value={r.hsts?.data?.preload ? 'Yes' : 'No'} />
                </>
              )}
            </AnalysisCard>

            {/* DNSSEC */}
            <AnalysisCard title="DNSSEC" icon={ShieldCheck} status={r.dnssec?.status || (r.dnssec?.data?.enabled ? 'success' : 'warning')}>
              <DataItem 
                label="DNSSEC" 
                value={r.dnssec?.data?.enabled ? 'Abilitato' : (r.dnssec?.data?.status === 'warning_aaaa_ns' ? 'Non configurato' : 'Non configurato')} 
                status={r.dnssec?.data?.enabled ? 'good' : (r.dnssec?.data?.status === 'warning_aaaa_ns' ? 'info' : 'warn')} 
              />
              {r.dnssec?.data?.details?.has_ds && (
                <DataItem label="DS Record" value="Presente" status="good" />
              )}
              {r.dnssec?.data?.details?.has_dnskey && (
                <DataItem label="DNSKEY" value="Presente" status="good" />
              )}
              {r.dnssec?.data?.details?.ns_aaaa_issue && (
                <div className="mt-2 p-2 bg-blue-900/30 rounded border border-blue-700/50">
                  <p className="text-xs text-blue-300 font-medium">⚠️ Warning AAAA sui NS</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {r.dnssec?.data?.details?.ns_without_aaaa?.length || 0} di {r.dnssec?.data?.details?.ns_servers?.length || 0} NS senza record AAAA (IPv6)
                  </p>
                  {r.dnssec?.data?.details?.ns_without_aaaa?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1 truncate">
                      NS: {r.dnssec?.data?.details?.ns_without_aaaa?.slice(0, 2).join(', ')}{r.dnssec?.data?.details?.ns_without_aaaa?.length > 2 ? '...' : ''}
                    </p>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">{safe(r.dnssec?.data?.message, 'No DNSSEC info')}</p>
            </AnalysisCard>

            {/* Crawl Rules */}
            <AnalysisCard title="Crawl Rules" icon={Bot}>
              <DataItem label="robots.txt" value={r.robotsTxt?.data?.exists ? 'Found' : 'Not Found'} status={r.robotsTxt?.data?.exists ? 'good' : 'warn'} />
              <DataItem label="Sitemap" value={r.sitemap?.data?.exists ? 'Found' : 'Not Found'} status={r.sitemap?.data?.exists ? 'good' : 'warn'} />
            </AnalysisCard>

            {/* Whois */}
            <AnalysisCard title="Whois Lookup" icon={Search}>
              <DataItem label="Registrar" value={safe(r.whois?.data?.registrar)} />
              <DataItem label="Created" value={safe(r.whois?.data?.createdDate)} />
              <DataItem label="Expires" value={safe(r.whois?.data?.expiryDate)} />
              <DataItem label="Organization" value={safe(r.whois?.data?.registrantOrg)} />
            </AnalysisCard>

            {/* Tech Stack */}
            <AnalysisCard title="Tech Stack" icon={Layers}>
              <div className="space-y-2">
                {safeArr(r.techStack?.data?.technologies).slice(0,6).map((tech, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-[#0f0f1a] rounded">
                    <span className="text-white text-xs">{tech.name || tech}</span>
                    <span className="text-xs text-gray-500">{tech.category || 'Other'}</span>
                  </div>
                ))}
                {safeArr(r.techStack?.data?.technologies).length === 0 && <span className="text-gray-500 text-xs">Nessuna tecnologia rilevata</span>}
              </div>
            </AnalysisCard>

            {/* Server Status */}
            <AnalysisCard title="Server Status" icon={Activity} status={r.uptimeStatus?.data?.isUp !== false ? 'success' : 'error'}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-3 h-3 rounded-full ${r.uptimeStatus?.data?.isUp !== false ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                <span className={`font-medium ${r.uptimeStatus?.data?.isUp !== false ? 'text-emerald-400' : 'text-red-400'}`}>
                  {r.uptimeStatus?.data?.isUp !== false ? 'Online' : 'Offline'}
                </span>
              </div>
              <DataItem label="Response Time" value={safe(r.uptimeStatus?.data?.responseTime)} />
              <DataItem label="Uptime (30d)" value={safe(r.uptimeStatus?.data?.uptime30Days, '99.9%')} status="good" />
            </AnalysisCard>

            {/* Carbon Footprint */}
            <AnalysisCard title="Carbon Footprint" icon={Leaf}>
              <DataItem label="CO₂ per Visit" value={safe(r.carbonFootprint?.data?.co2PerView)} />
              <DataItem label="Cleaner Than" value={safe(r.carbonFootprint?.data?.cleanerThan)} />
              <DataItem label="Green Hosting" value={r.carbonFootprint?.data?.greenHosting ? 'Yes' : 'No'} status={r.carbonFootprint?.data?.greenHosting ? 'good' : 'warn'} />
            </AnalysisCard>

            {/* Global Ranking */}
            <AnalysisCard title="Global Ranking" icon={TrendingUp}>
              <div className="text-center mb-3">
                <div className="text-3xl font-bold text-cyan-400">#{(r.globalRank?.data?.global || 0).toLocaleString()}</div>
                <div className="text-xs text-gray-500">Worldwide</div>
              </div>
              <DataItem label="Country Rank" value={`#${(r.globalRank?.data?.countryRank || 0).toLocaleString()}`} />
              <DataItem label="Category" value={safe(r.globalRank?.data?.category)} />
            </AnalysisCard>

            {/* Threats */}
            <AnalysisCard title="Threats & Blocklist" icon={Bug} status={r.threats?.data?.safe !== false ? 'success' : 'error'}>
              <div className="flex items-center gap-2 mb-3">
                {r.threats?.data?.safe !== false ? (
                  <><CheckCircle className="h-8 w-8 text-emerald-400" /><span className="text-emerald-400 font-medium">Clean</span></>
                ) : (
                  <><XCircle className="h-8 w-8 text-red-400" /><span className="text-red-400 font-medium">Threats Detected</span></>
                )}
              </div>
              <DataItem label="Google Safe Browsing" value={safe(r.threats?.data?.googleSafeBrowsing, 'Clean')} status="good" />
            </AnalysisCard>

            {/* Archive History */}
            <AnalysisCard title="Archive History" icon={Archive}>
              <DataItem label="First Capture" value={safe(r.archiveHistory?.data?.firstCapture)} />
              <DataItem label="Last Capture" value={safe(r.archiveHistory?.data?.lastCapture)} />
              <DataItem label="Total Captures" value={(r.archiveHistory?.data?.totalCaptures || 0).toLocaleString()} />
            </AnalysisCard>

            {/* Screenshot - Real site preview */}
            <AnalysisCard title="Screenshot" icon={Camera} size="large">
              <div className="bg-[#0f0f1a] rounded-lg overflow-hidden">
                <div className="relative">
                  {/* Website thumbnail using screenshot service */}
                  <img 
                    src={`https://api.microlink.io/?url=https://${results.domain}&screenshot=true&meta=false&embed=screenshot.url`}
                    alt={`Screenshot di ${results.domain}`}
                    className="w-full h-48 object-cover object-top"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  {/* Fallback if image fails */}
                  <div className="hidden w-full h-48 flex-col items-center justify-center bg-[#0f0f1a]">
                    <Camera className="h-12 w-12 text-gray-600 mb-2" />
                    <span className="text-gray-500 text-sm">Preview non disponibile</span>
                  </div>
                  {/* Overlay with site info */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                    <div className="flex items-center gap-2">
                      <img 
                        src={`https://www.google.com/s2/favicons?domain=${results.domain}&sz=32`}
                        alt="favicon"
                        className="w-4 h-4"
                      />
                      <span className="text-white text-xs font-medium">{results.domain}</span>
                    </div>
                  </div>
                </div>
                <div className="p-3 flex items-center justify-center">
                  <span className="text-xs text-gray-400">Catturato: {new Date().toLocaleDateString('it-IT')}</span>
                </div>
              </div>
            </AnalysisCard>

            {/* Redirect Chain */}
            <AnalysisCard title="Redirect Chain" icon={Shuffle}>
              <DataItem label="Total Redirects" value={safe(r.redirectChain?.data?.totalRedirects, 0)} />
              <DataItem label="Final URL" value={safe(r.redirectChain?.data?.finalUrl)} />
              {(r.redirectChain?.data?.redirects || []).slice(0, 3).map((rd, i) => (
                <DataItem key={i} label={`Step ${rd.step}`} value={rd.url?.substring(0, 40) + '...'} />
              ))}
            </AnalysisCard>

            {/* DNS Server */}
            <AnalysisCard title="DNS Server" icon={Database}>
              <DataItem label="Response Time" value={safe(r.dnsServer?.data?.responseTime)} />
              <DataItem label="DNSSEC" value={r.dnsServer?.data?.dnssec ? 'Enabled' : 'Disabled'} status={r.dnsServer?.data?.dnssec ? 'good' : 'warning'} />
              {(r.dnsServer?.data?.servers || []).slice(0, 2).map((srv, i) => (
                <DataItem key={i} label={`Server ${i + 1}`} value={srv.ip || 'N/A'} />
              ))}
            </AnalysisCard>

            {/* Sitemap */}
            <AnalysisCard title="Sitemap" icon={Map} status={r.sitemap?.data?.exists ? 'success' : 'warning'}>
              <DataItem label="Exists" value={r.sitemap?.data?.exists ? 'Yes' : 'No'} status={r.sitemap?.data?.exists ? 'good' : 'warning'} />
              <DataItem label="URL" value={safe(r.sitemap?.data?.url)} />
              <DataItem label="Entries" value={safe(r.sitemap?.data?.entries, 0)} />
            </AnalysisCard>

            {/* Site Features */}
            <AnalysisCard title="Site Features" icon={Cpu}>
              <div className="space-y-2">
                {(r.siteFeatures?.data?.analytics || []).length > 0 && (
                  <DataItem label="Analytics" value={(r.siteFeatures?.data?.analytics || []).join(', ')} />
                )}
                {(r.siteFeatures?.data?.cms || []).length > 0 && (
                  <DataItem label="CMS" value={(r.siteFeatures?.data?.cms || []).join(', ')} />
                )}
                {(r.siteFeatures?.data?.frameworks || []).length > 0 && (
                  <DataItem label="Frameworks" value={(r.siteFeatures?.data?.frameworks || []).join(', ')} />
                )}
                {(r.siteFeatures?.data?.cdns || []).length > 0 && (
                  <DataItem label="CDN" value={(r.siteFeatures?.data?.cdns || []).join(', ')} />
                )}
                {!(r.siteFeatures?.data?.analytics?.length || r.siteFeatures?.data?.cms?.length || r.siteFeatures?.data?.frameworks?.length) && (
                  <DataItem label="Status" value="Detection in progress..." />
                )}
              </div>
            </AnalysisCard>

            {/* Server Info */}
            <AnalysisCard title="Server Info" icon={HardDrive}>
              <DataItem label="Server" value={safe(r.serverInfo?.data?.server)} />
              <DataItem label="Powered By" value={safe(r.serverInfo?.data?.poweredBy)} />
              <DataItem label="Via" value={safe(r.serverInfo?.data?.via)} />
              <DataItem label="Platform" value={safe(r.serverInfo?.data?.platform)} />
            </AnalysisCard>

            {/* Domain Info */}
            <AnalysisCard title="Domain Info" icon={Globe2}>
              <DataItem label="Registrar" value={safe(r.domainInfo?.data?.registrar)} />
              <DataItem label="Age" value={safe(r.domainInfo?.data?.age)} />
              <DataItem label="Expires" value={safe(r.domainInfo?.data?.expires)} />
              {(r.domainInfo?.data?.nameservers || []).slice(0, 2).map((ns, i) => (
                <DataItem key={i} label={`NS ${i + 1}`} value={ns} />
              ))}
            </AnalysisCard>

            {/* Associated Hosts */}
            <AnalysisCard title="Associated Hosts" icon={Users}>
              <DataItem label="Total Hosts" value={safe(r.associatedHosts?.data?.total, 0)} />
              {(r.associatedHosts?.data?.hosts || []).slice(0, 4).map((host, i) => (
                <DataItem key={i} label={`Host ${i + 1}`} value={host} />
              ))}
            </AnalysisCard>

            {/* TLS Security Config */}
            <AnalysisCard title="TLS Security Config" icon={Key}>
              <DataItem label="Protocol" value={safe(r.tlsConfig?.data?.protocol)} />
              <DataItem label="Key Exchange" value={safe(r.tlsConfig?.data?.keyExchange)} />
              <DataItem label="Cipher" value={safe(r.tlsConfig?.data?.cipher)} />
              <DataItem label="Cert Signature" value={safe(r.tlsConfig?.data?.certSignature)} />
            </AnalysisCard>

            {/* TLS Handshake */}
            <AnalysisCard title="TLS Handshake" icon={Wifi}>
              <DataItem label="Time" value={safe(r.tlsHandshake?.data?.time)} />
              <DataItem label="Protocol" value={safe(r.tlsHandshake?.data?.protocol)} />
              <DataItem label="Session Resumed" value={r.tlsHandshake?.data?.resumed ? 'Yes' : 'No'} />
              <DataItem label="OCSP Stapling" value={r.tlsHandshake?.data?.ocsp ? 'Yes' : 'No'} />
            </AnalysisCard>

            {/* Block Detection */}
            <AnalysisCard title="Block Detection" icon={Ban} status={r.blockDetection?.data?.blocked ? 'error' : 'success'}>
              <DataItem label="Blocked" value={r.blockDetection?.data?.blocked ? 'Yes' : 'No'} status={r.blockDetection?.data?.blocked ? 'bad' : 'good'} />
              {(r.blockDetection?.data?.lists || []).slice(0, 3).map((list, i) => (
                <DataItem key={i} label={list.name} value={list.detected ? 'Detected' : 'Clean'} status={list.detected ? 'bad' : 'good'} />
              ))}
            </AnalysisCard>

            {/* Traceroute */}
            <AnalysisCard title="Traceroute" icon={Route}>
              <DataItem label="Total Hops" value={safe(r.traceRoute?.data?.totalHops, 0)} />
              <DataItem label="Avg Latency" value={safe(r.traceRoute?.data?.avgLatency)} />
              {(r.traceRoute?.data?.hops || []).slice(0, 4).map((hop, i) => (
                <DataItem key={i} label={`Hop ${hop.hop}`} value={`${hop.ip} (${hop.time})`} />
              ))}
            </AnalysisCard>

            {/* TXT Records */}
            <AnalysisCard title="TXT Records" icon={FileCode}>
              {(r.txtRecords?.data?.records || []).slice(0, 4).map((rec, i) => {
                const recValue = typeof rec.value === 'string' ? rec.value : (Array.isArray(rec.value) ? rec.value.join('') : JSON.stringify(rec.value || ''));
                return (
                  <div key={i} className="mb-2">
                    <span className="text-xs text-cyan-400">[{rec.type || 'TXT'}]</span>
                    <p className="text-xs text-gray-300 truncate">{recValue?.substring(0, 60)}...</p>
                  </div>
                );
              })}
              {!(r.txtRecords?.data?.records?.length) && (
                <DataItem label="Status" value="No TXT records found" />
              )}
            </AnalysisCard>

            {/* Linked Pages */}
            <AnalysisCard title="Linked Pages" icon={Link2}>
              <DataItem label="Internal Links" value={safe(r.linkedPages?.data?.internal?.count, 0)} />
              <DataItem label="External Links" value={safe(r.linkedPages?.data?.external?.count, 0)} />
              <DataItem label="Broken Links" value={safe(r.linkedPages?.data?.broken?.count, 0)} status={r.linkedPages?.data?.broken?.count > 0 ? 'warning' : 'good'} />
            </AnalysisCard>

            {/* Social Tags */}
            <AnalysisCard title="Social Tags" icon={Share2}>
              <DataItem label="Title" value={safe(r.socialTags?.data?.title)} />
              <DataItem label="Description" value={safe(r.socialTags?.data?.description)?.substring(0, 50) + '...'} />
              <DataItem label="OG Image" value={r.socialTags?.data?.ogTags?.['og:image'] ? 'Set' : 'Not set'} status={r.socialTags?.data?.ogTags?.['og:image'] ? 'good' : 'warning'} />
            </AnalysisCard>

            {/* Security.txt */}
            <AnalysisCard title="Security.txt" icon={FileText} status={r.securityTxt?.data?.exists ? 'success' : 'info'}>
              <DataItem label="Exists" value={r.securityTxt?.data?.exists ? 'Yes' : 'No'} status={r.securityTxt?.data?.exists ? 'good' : 'neutral'} />
              {r.securityTxt?.data?.exists && (
                <>
                  <DataItem label="Contact" value={safe(r.securityTxt?.data?.contact)} />
                  <DataItem label="Expires" value={safe(r.securityTxt?.data?.expires)} />
                </>
              )}
            </AnalysisCard>

          </div>

          {/* Analysis Summary */}
          <AnalysisSummary results={r} domain={results.domain} />

          {/* Premium Services Banner - Bottom */}
          <div className="mt-8 bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-500/30 rounded-xl p-5">
            <h3 className="text-lg font-bold text-white mb-1">Vuoi un'analisi più approfondita?</h3>
            <p className="text-gray-300 text-sm mb-4">
              Scopri le vulnerabilità nascoste e proteggi la tua azienda con i nostri servizi premium
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowPremiumModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all flex items-center gap-2"
              >
                <Shield className="h-4 w-4" /> Vulnerability Assessment
              </button>
              <button
                onClick={() => setShowPremiumModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all flex items-center gap-2"
              >
                <Search className="h-4 w-4" /> Cyber Investigation
              </button>
            </div>
          </div>

          {/* Footer */}
          <footer className="mt-12 pt-8 border-t border-gray-800/50 text-center">
            <p className="text-gray-500 text-sm">© {new Date().getFullYear()} Smart Info Cyber Security</p>
          </footer>
        </div>
      </main>

      {/* PDF Upsell Banner */}
      {showPdfUpsellBanner && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a2e] border border-gray-700 rounded-2xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <Sparkles className="h-6 w-6 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Vuoi un'analisi più approfondita?</h3>
              </div>
              <button 
                onClick={() => setShowPdfUpsellBanner(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content */}
            <p className="text-gray-300 mb-5 text-sm leading-relaxed">
              Il report gratuito include le informazioni base. I nostri <span className="text-red-400 font-semibold">servizi premium</span> offrono:
            </p>
            
            <ul className="space-y-2 mb-6">
              <li className="flex items-center gap-2 text-gray-300 text-sm">
                <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                <span>Vulnerability Assessment completo</span>
              </li>
              <li className="flex items-center gap-2 text-gray-300 text-sm">
                <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                <span>Cyber Risk Score e impatto economico</span>
              </li>
              <li className="flex items-center gap-2 text-gray-300 text-sm">
                <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                <span>Piano di remediation personalizzato</span>
              </li>
              <li className="flex items-center gap-2 text-gray-300 text-sm">
                <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                <span>Supporto tecnico dedicato</span>
              </li>
            </ul>
            
            {/* Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={openPremiumServices}
                className="w-full py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-red-500/25"
              >
                <Zap className="h-5 w-5" />
                Scopri i Servizi Premium
              </button>
              <button
                onClick={continueFreeDownload}
                className="w-full py-3 bg-gray-700/50 hover:bg-gray-700 text-gray-300 hover:text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-all"
              >
                <Download className="h-4 w-4" />
                Continua con il Report Gratuito
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Services Modal */}
      <PremiumServicesModal 
        isOpen={showPremiumModal} 
        onClose={() => setShowPremiumModal(false)}
        analyzedDomain={results?.domain}
      />
    </div>
  );
};

export default ResultsPage;
